import React, { useState } from "react"
import { Routes, Route, useNavigate } from "react-router-dom"
import {Menu, X, Phone, Mail, MapPin, Clock, ChevronRight, Shield, Zap, Users, Award, Check, Star, MessageCircleDashed as MessageCircle, Calendar, Car, Sparkles} from 'lucide-react'
import toast, { Toaster } from "react-hot-toast"
import { lumi } from "./lib/lumi"
import ChatWidget from "./components/ChatWidget"
import QuickActions from "./components/QuickActions"
import FleetQuoteForm from "./components/FleetQuoteForm"
import InteriorWrapTrimPage from "./pages/InteriorWrapTrimPage"
import RacingStripesGraphicsPage from "./pages/RacingStripesGraphicsPage"
import TeslaPpfSanDiegoPage from "./pages/TeslaPpfSanDiegoPage"
import PaintProtectionFilmSanDiegoPage from "./pages/PaintProtectionFilmSanDiegoPage"
import CarWrapSanDiegoPage from "./pages/CarWrapSanDiegoPage"
import ChromeDeleteSanDiegoPage from "./pages/ChromeDeleteSanDiegoPage"
import WindowTintSanDiegoPage from "./pages/WindowTintSanDiegoPage"
import PpfLaJollaPage from "./pages/PpfLaJollaPage"
import PpfDelMarPage from "./pages/PpfDelMarPage"
import PpfPowayPage from "./pages/PpfPowayPage"
import PpfSanMarcosPage from "./pages/PpfSanMarcosPage"
import PpfEscondidoPage from "./pages/PpfEscondidoPage"
import PpfSolanaBeachPage from "./pages/PpfSolanaBeachPage"
import TeslaModelYPpfPage from "./pages/TeslaModelYPpfPage"
import TeslaModel3PpfPage from "./pages/TeslaModel3PpfPage"
import TeslaModelSPpfPage from "./pages/TeslaModelSPpfPage"
import TeslaModelXPpfPage from "./pages/TeslaModelXPpfPage"
import TeslaWrapSanDiegoPage from "./pages/TeslaWrapSanDiegoPage"
import ColorChangeWrapSanDiegoPage from "./pages/ColorChangeWrapSanDiegoPage"
import ColoredPpfSanDiegoPage from "./pages/ColoredPpfSanDiegoPage"
import VehicleWrapCostSanDiegoPage from "./pages/VehicleWrapCostSanDiegoPage"
import CeramicCoatingSanDiegoPage from "./pages/CeramicCoatingSanDiegoPage"
import PpfVsCeramicCoatingSanDiegoPage from "./pages/PpfVsCeramicCoatingSanDiegoPage"
import BmwPpfSanDiegoPage from "./pages/BmwPpfSanDiegoPage"
import CorvettePpfSanDiegoPage from "./pages/CorvettePpfSanDiegoPage"
import BlogPpfGuidePage from "./pages/BlogPpfGuidePage"
import BlogPpfVsCeramicPage from "./pages/BlogPpfVsCeramicPage"
import BlogPpfLongevityPage from "./pages/BlogPpfLongevityPage"
import ThankYouPage from "./pages/ThankYouPage"
import CanonicalTag from "./components/CanonicalTag"
import { Helmet } from "react-helmet-async"
import { AppointmentBookingForm } from "./components/AppointmentBookingForm"
import BeforeAfterSlider from "./components/BeforeAfterSlider"

// Contact Page - MUST be defined outside App() to prevent re-mounting
const ContactPage = () => {
  const [contactFormData, setContactFormData] = useState({
    name: "",
    phone: "",
    email: "",
    vehicle: "",
    service: "",
    message: ""
  })
  const [activeTab, setActiveTab] = useState<'quote' | 'appointment'>('quote')

  return (
    <div className="pt-20">
      <CanonicalTag url="/contact" />
      {/* Contact Hero */}
      <section className="bg-gradient-to-br from-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Get In Touch</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Ready to protect your vehicle? Request a quote or book an appointment online
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column: Forms with Tabs */}
            <div>
              {/* Tab Buttons */}
              <div className="flex gap-4 mb-8">
                <button
                  onClick={() => setActiveTab('quote')}
                  className={`flex-1 py-4 px-6 rounded-lg font-bold transition-all ${
                    activeTab === 'quote'
                      ? 'bg-orange-500 text-white shadow-lg'
                      : 'bg-gray-900 text-gray-300 border border-cyan-500/20 hover:border-cyan-500'
                  }`}
                >
                  Request a Quote
                </button>
                <button
                  onClick={() => setActiveTab('appointment')}
                  className={`flex-1 py-4 px-6 rounded-lg font-bold transition-all ${
                    activeTab === 'appointment'
                      ? 'bg-orange-500 text-white shadow-lg'
                      : 'bg-gray-900 text-gray-300 border border-cyan-500/20 hover:border-cyan-500'
                  }`}
                >
                  Book an Appointment
                </button>
              </div>

              {/* Quote Form */}
              {activeTab === 'quote' && (
                <div className="bg-gray-900 rounded-2xl shadow-xl p-8 border border-cyan-500/20">
                  <h2 className="text-3xl font-bold mb-6 text-white">Request a Quote</h2>
                  <p className="text-gray-300 mb-6">Not sure what you need? Fill out this form and we'll contact you to discuss your project and provide a custom quote.</p>
              <form onSubmit={async (e) => {
                e.preventDefault()
                
                if (!contactFormData.name || !contactFormData.phone || !contactFormData.email) {
                  toast.error("Please fill in all required fields")
                  return
                }

                try {
                  const response = await lumi.functions.invoke("contact-submit", {
                    method: "POST",
                    body: contactFormData
                  })

                  if (response.success) {
                    setContactFormData({ name: "", phone: "", email: "", vehicle: "", service: "", message: "" })
                    navigate("/thank-you")
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  } else {
                    toast.error("Submission failed. Please try again or call us directly.")
                  }
                } catch (error) {
                  console.error("Contact submission error:", error)
                  toast.error("Submission failed. Please try again or call us directly.")
                }
              }} className="space-y-6">

                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-gray-300 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    value={contactFormData.name}
                    onChange={(e) => {
                      const { name, value } = e.target
                      setContactFormData(prev => ({ ...prev, [name]: value }))
                    }}
                    required
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
                    placeholder="John Smith"
                  />
                </div>
                
                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-300 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="contact-phone"
                    name="phone"
                    value={contactFormData.phone}
                    onChange={(e) => {
                      const { name, value } = e.target
                      setContactFormData(prev => ({ ...prev, [name]: value }))
                    }}
                    required
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
                    placeholder="858-282-8119"
                  />
                </div>
                
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    value={contactFormData.email}
                    onChange={(e) => {
                      const { name, value } = e.target
                      setContactFormData(prev => ({ ...prev, [name]: value }))
                    }}
                    required
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="contact-vehicle" className="block text-sm font-medium text-gray-300 mb-2">
                    Vehicle (Year, Make, Model)
                  </label>
                  <input
                    type="text"
                    id="contact-vehicle"
                    name="vehicle"
                    value={contactFormData.vehicle}
                    onChange={(e) => {
                      const { name, value } = e.target
                      setContactFormData(prev => ({ ...prev, [name]: value }))
                    }}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
                    placeholder="2023 Tesla Model 3"
                  />
                </div>

                <div>
                  <label htmlFor="contact-service" className="block text-sm font-medium text-gray-300 mb-2">
                    Service Interested In
                  </label>
                  <select
                    id="contact-service"
                    name="service"
                    value={contactFormData.service}
                    onChange={(e) => {
                      const { name, value } = e.target
                      setContactFormData(prev => ({ ...prev, [name]: value }))
                    }}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
                  >
                    <option value="">Select a service</option>
                    <option value="ppf">Paint Protection Film</option>
                    <option value="colored-ppf">Colored Paint Protection Film</option>
                    <option value="ceramic-coating">Ceramic Coating</option>
                    <option value="window-tint">Window Tinting</option>
                    <option value="vehicle-wrap">Vehicle Wrap</option>
                    <option value="chrome-delete">Chrome Delete</option>
                    <option value="fleet-wraps">Fleet Wraps</option>
                    <option value="caliper-painting">Caliper Painting</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-gray-300 mb-2">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={contactFormData.message}
                    onChange={(e) => {
                      const { name, value } = e.target
                      setContactFormData(prev => ({ ...prev, [name]: value }))
                    }}

                    rows={4}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
                    placeholder="Tell us about your project..."
                  />
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-6 rounded-lg transition-all transform hover:scale-105 shadow-lg"
                >
                  Send Message
                </button>
                
                <p className="text-xs text-gray-400 text-center">
                  We'll respond within 24 hours
                </p>
                

              </form>
                </div>
              )}

              {/* Appointment Booking Form */}
              {activeTab === 'appointment' && (
                <AppointmentBookingForm />
              )}
            </div>

            {/* Right Column: Contact Information */}
            <div className="space-y-8">
              <div className="bg-gray-900 rounded-2xl shadow-xl p-8 border border-cyan-500/20">
                <h2 className="text-3xl font-bold mb-6 text-white">Visit Our Shop</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-cyan-500/20 p-3 rounded-lg border border-cyan-500">
                      <MapPin className="text-cyan-400" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1">Location</h3>
                      <p className="text-gray-300">2244 South Santa Fe Avenue Suite A7<br />Vista, CA 92084<br />Serving all of San Diego County</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-cyan-500/20 p-3 rounded-lg border border-cyan-500">
                      <Phone className="text-cyan-400" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1">Phone</h3>
                      <a href="tel:858-282-8119" className="text-gray-300 hover:text-cyan-400 transition">
                        858-282-8119
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-cyan-500/20 p-3 rounded-lg border border-cyan-500">
                      <Mail className="text-cyan-400" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1">Email</h3>
                      <a href="mailto:mrwrapmatt@gmail.com" className="text-gray-300 hover:text-cyan-400 transition">
                        mrwrapmatt@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-cyan-500/20 p-3 rounded-lg border border-cyan-500">
                      <Clock className="text-cyan-400" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1">Hours</h3>
                      <p className="text-gray-300">
                        Monday - Friday: 8:00 AM - 6:00 PM<br />
                        Saturday: 9:00 AM - 4:00 PM<br />
                        Sunday: By Appointment
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl shadow-xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
                <p className="mb-6">
                  Call us now for immediate assistance or to schedule an in-person consultation at our Vista shop.
                </p>
                <a 
                  href="tel:858-282-8119"
                  className="block w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-6 rounded-lg transition-all transform hover:scale-105 shadow-lg text-center"
                >
                  Call Now: 858-282-8119
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

function App() {
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false)
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState("home")
  const [serviceSection, setServiceSection] = useState<string | null>(null)
  const [selectedBrand, setSelectedBrand] = useState("all")
  const [closeDropdownTimeout, setCloseDropdownTimeout] = useState<NodeJS.Timeout | null>(null)
  const [currentReviewSlide, setCurrentReviewSlide] = useState(0)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const [currentPPFSlide, setCurrentPPFSlide] = useState(0)
  const [ppfTouchStart, setPPFTouchStart] = useState(0)
  const [ppfTouchEnd, setPPFTouchEnd] = useState(0)
  // Landing page forms state (vehicle/location pages)
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    vehicle: "",
    service: "",
    message: ""
  })



  // Landing page form submit (vehicle/location pages)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name || !formData.phone || !formData.email) {
      toast.error("Please fill in all required fields")
      return
    }

    try {
      const response = await lumi.functions.invoke("contact-submit", {
        method: "POST",
        body: formData
      })

      if (response.success) {
        toast.success("Thank you — we've received your request and will be in touch shortly.")
        setFormData({ name: "", phone: "", email: "", vehicle: "", service: "", message: "" })
      } else {
        toast.error("Submission failed. Please try again or call us directly.")
      }
    } catch (error) {
      console.error("Contact submission error:", error)
      toast.error("Submission failed. Please try again or call us directly.")
    }
  }



  // Landing page form change handler
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }



  const navigateTo = (page: string) => {
    // If currently on a real route (like /ppf-vista or /north-county-vinyl-wraps or /paint-protection-film or /vinyl-wraps or /commercial-fleet-wraps or /window-tinting), navigate to / first
    if (window.location.pathname.startsWith("/ppf-") || window.location.pathname === "/north-county-vinyl-wraps" || window.location.pathname === "/paint-protection-film" || window.location.pathname === "/vinyl-wraps" || window.location.pathname === "/commercial-fleet-wraps" || window.location.pathname === "/window-tinting" || window.location.pathname === "/chrome-delete" || window.location.pathname === "/caliper-painting" || window.location.pathname === "/headlight-taillight-tint" || window.location.pathname === "/interior-wrap-trim" || window.location.pathname === "/racing-stripes-graphics") {
      navigate("/")
    }
    setCurrentPage(page)
    setMobileMenuOpen(false)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const carBrands = [
    { name: "Tesla", models: ["Model 3", "Model S", "Model X", "Model Y"], image: "https://static.lumi.new/75/75a2e5c024c25d5abc5286326335c10a.webp" },
    { name: "Cybertruck", models: ["Cybertruck"], image: "https://static.lumi.new/ff/ff9ce8ba7bae51f4631031e8c8c79cf6.webp" },
    { name: "Porsche", models: ["911", "Taycan", "Cayenne", "Macan"], image: "https://static.lumi.new/8a/8a6d7d9b53f8da7859f61daa93cb2bbd.webp" },
    { name: "Mercedes", models: ["G-Wagon", "C-Class", "E-Class", "S-Class"], image: "https://static.lumi.new/b1/b16a6c9ee134b8fa61c6ebe3a2032a7a.webp" },
    { name: "BMW", models: ["M3", "M4", "M5", "X5"], image: "https://static.lumi.new/e3/e333d8989785826da29b7c8cfc256dca.webp" },
    { name: "Audi", models: ["A4", "A6", "Q5", "Q7"], image: "https://static.lumi.new/32/326d128dc64c0316595e6905e1997e8a.webp" },
    { name: "Maserati", models: ["Ghibli", "Levante", "Quattroporte", "GranTurismo"], image: "https://static.lumi.new/5b/5bde8a60bc95c8d032447e94cfa13ddc.webp" },
    { name: "RAM", models: ["1500", "2500", "3500", "TRX"], image: "https://static.lumi.new/b2/b291d2d6e61d538967c84eaf565fba1d.webp" },
    { name: "Rolls Royce", models: ["Dawn", "Wraith", "Ghost", "Cullinan"], image: "https://static.lumi.new/9c/9c16d932751f74587a06afcba18dbd78.webp" },
    { name: "Range Rover", models: ["Sport", "Velar", "Evoque", "Defender"], image: "https://static.lumi.new/3f/3f55d3a9c3a7cb148dc864f4654c6428.webp" },
    { name: "Chevy", models: ["Silverado", "Camaro", "Corvette", "Tahoe"], image: "https://static.lumi.new/cd/cd37eb82ccebf83e953c4ca018d23c62.webp" },
    { name: "Corvette", models: ["Stingray", "C8", "Z06", "ZR1"], image: "https://static.lumi.new/1c/1c91ccf9aae3ffd30b7ec6818e7c1be9.webp" },
    { name: "Mini Cooper", models: ["Cooper S", "John Cooper Works", "Countryman"], image: "https://static.lumi.new/e6/e659d3e6a65094e8bda2aa02e59b8175.webp" },
    { name: "Motorcycle", models: ["Sport Bikes", "Cruisers", "Custom Bikes"], image: "https://static.lumi.new/2a/2ac0d58ffedc960e351096946076ecca.webp" },
    { name: "RZR", models: ["Pro XP", "Turbo R", "XP 1000", "Trail"], image: "https://static.lumi.new/ec/ecf111d72314812c63caf042d438cde9.webp" },
    { name: "Commercial Wraps", models: ["Fleet Vehicles", "Box Trucks", "Vans", "Trailers"], image: "https://static.lumi.new/e2/e2809b1b88e8c9581583cb86512b3d01.webp" },
  ]

  const services = [
    {
      title: "Paint Protection Film",
      description: "Premium self-healing film with advanced protection technology",
      image: "https://static.lumi.new/56/56cd8dc39eb01f582efecfa70381ab9b.webp",
      features: ["Manufacturer Warranty", "Self-Healing Technology", "Crystal Clear Finish", "Precision Installation"]
    },
    {
      title: "Window Tinting",
      description: "Premium ceramic tint for heat rejection & privacy",
      image: "https://static.lumi.new/c3/c3667fbc51078a3f60fd03b0ea2f8b3d.webp",
      features: ["99% UV Protection", "Lifetime Warranty", "Ceramic Technology", "No Signal Interference"]
    },
    {
      title: "Vehicle Wraps",
      description: "Complete color changes & custom designs",
      image: "https://static.lumi.new/cc/cce71ddf6b6089aa064e68b2b617df57.webp",
      features: ["3M & Avery Materials", "Full or Partial Wraps", "Removable & Reversible", "Custom Graphics"]
    },
    {
      title: "Chrome Delete",
      description: "Modern blacked-out aesthetic",
      image: "https://static.lumi.new/9f/9f0bf5df0736ae5d758d7327eb952bff.webp",
      features: ["Gloss or Matte Finish", "Perfect Fit", "Preserves Original Chrome", "Quick Installation"]
    },
    {
      title: "Fleet Wraps",
      description: "Professional branding for your business vehicles",
      image: "https://static.lumi.new/59/59d04c24c2308e0666a9f0e83e60782e.webp",
      features: ["Custom Logo Design", "Durable Materials", "Consistent Branding", "Volume Discounts"]
    },
    {
      title: "Storefront Graphics",
      description: "Eye-catching window graphics & signage for your business",
      image: "https://static.lumi.new/19/19dda6195a3f0b3faebf9ba77ab1aefc.webp",
      features: ["Custom Design", "High-Impact Visuals", "Weather Resistant", "Professional Installation"]
    },
    {
      title: "Powder Coating",
      description: "Durable powder coating for wheels and automotive parts",
      image: "https://static.lumi.new/14/1445d6d85c5dc0fa99018acd811775ba.webp",
      features: ["Superior Durability", "Custom Colors", "Wheels & Parts", "Professional Finish"]
    },
    {
      title: "Caliper Painting",
      description: "Professional brake caliper painting in any color",
      image: "https://static.lumi.new/94/946b2756c364d2551a7fcbf0165a47b9.webp",
      features: ["Any Color Available", "Custom Decals Printed", "In-House Design", "Heat-Resistant Paint"]
    }
  ]

  const gallery = [
    { url: "https://static.lumi.new/13/13629e80aa9870537fe8adc495e1a951.webp", title: "BMW X5 - Matte Gray PPF Installation in Shop", brand: "BMW" },
    { url: "https://static.lumi.new/81/81053039ab225476e341cd0250398f52.webp", title: "Maserati Ghibli - White Wrap in Shop", brand: "Maserati" },
    { url: "https://static.lumi.new/c7/c78cb81a446620a625d8454e3919a7cd.webp", title: "BMW M3 - Blue Wrap Detail", brand: "BMW" },
    { url: "https://static.lumi.new/49/49b044a85fefd1081b78f6461187ed35.webp", title: "Cybertruck - Hood Wrap Detail", brand: "Cybertruck" },
    { url: "https://static.lumi.new/6e/6ea5e690fc983ab1fb256192b4505cec.webp", title: "BMW M3 - Chameleon Color Shift Wrap", brand: "BMW" },
    { url: "https://static.lumi.new/44/44db9d203660923d594de54b30995f0c.webp", title: "Range Rover - White with Black Hood", brand: "Range Rover" },
    { url: "https://static.lumi.new/3b/3b849b078f4a32e1663976d79744117d.webp", title: "Tesla Model 3 - Pink Wrap", brand: "Tesla" },
    { url: "https://static.lumi.new/0d/0d187c7d35932660a882bd902508abe9.webp", title: "Rolls Royce & Range Rover - Shop", brand: "Rolls Royce" },
    { url: "https://static.lumi.new/ae/aea200193d3c54e006500507f3468b16.webp", title: "Porsche Taycan - Matte Black Wrap", brand: "Porsche" },
    { url: "https://static.lumi.new/c5/c569c3f9888a7a4fed4a4801f7c73daa.webp", title: "Tesla Model 3 - PPF Install", brand: "Tesla" },
    { url: "https://static.lumi.new/ff/ff0e28b025c4939b79a561fc684dab73.webp", title: "White Sedan - Chrome Delete & Wrap", brand: "Luxury" },
    { url: "https://static.lumi.new/1e/1ec721c5e5e5c17a4cc89e1efdeaa254.webp", title: "Shop Interior - Tesla Wraps in Progress", brand: "Tesla" },
    { url: "https://static.lumi.new/37/3754650ba434f370def16e1f881dd864.webp", title: "BMW Wheels - Caliper Work", brand: "BMW" },
    { url: "https://static.lumi.new/9c/9c16d932751f74587a06afcba18dbd78.webp", title: "Rolls Royce - Black at Mr. Wrap Shop", brand: "Rolls Royce" },
    { url: "https://static.lumi.new/35/35c4c53e052b7e5a34ef0fa5e301fd78.webp", title: "Tesla - PPF Installation Process", brand: "Tesla" },
    { url: "https://static.lumi.new/73/7321f92d0061134ffef642174e244cc9.webp", title: "Mercedes - White PPF Front Detail", brand: "Mercedes" },
    { url: "https://static.lumi.new/db/db1a5a270d1e6dcb92ad91af0ba24fea.webp", title: "Tesla Model Y - Light Blue Wrap", brand: "Tesla" },
    { url: "https://static.lumi.new/b2/b291d2d6e61d538967c84eaf565fba1d.webp", title: "RAM Truck - Black Wrap in Shop", brand: "RAM" },
    { url: "https://static.lumi.new/bd/bd2ddd19557b59d3f408022e97e4aefa.webp", title: "Tesla - Ceramic Window Tint", brand: "Tesla" },
    { url: "https://static.lumi.new/b1/b16a6c9ee134b8fa61c6ebe3a2032a7a.webp", title: "Mercedes C63 - Matte Blue at Shop", brand: "Mercedes" },
    { url: "https://static.lumi.new/52/5234166494ce9fbf1dd6bad26161ad9e.webp", title: "Porsche Macan - White PPF Install", brand: "Porsche" },
    { url: "https://static.lumi.new/1f/1f7f79af63b46f26b301e38aa0529a29.webp", title: "BMW M4 - Matte Grey Detail", brand: "BMW" },
    { url: "https://static.lumi.new/c0/c0c3282ad420de67b177ad57d7624f1b.webp", title: "Custom Lime Green Wrap", brand: "Luxury" },
    { url: "https://static.lumi.new/cd/cd37eb82ccebf83e953c4ca018d23c62.webp", title: "Chevy Silverado - Matte Grey Wrap & Lift", brand: "Chevy" },
    { url: "https://static.lumi.new/50/506b46c1f21104cfd3e739fdc5066aa9.webp", title: "Rolls Royce - White with Dark Tint", brand: "Rolls Royce" },
    { url: "https://static.lumi.new/3f/3f55d3a9c3a7cb148dc864f4654c6428.webp", title: "Range Rover - Matte Black Wrap", brand: "Range Rover" },
    { url: "https://static.lumi.new/33/33e133aa1c2f8235148bf7b9bccabe05.webp", title: "Luxury SUV - Iridescent Chrome Wrap", brand: "Luxury" },
    { url: "https://static.lumi.new/c3/c3baa6df5270afa569a039a322e1876b.webp", title: "Tesla Model X - White with Dark Tint", brand: "Tesla" },
    { url: "https://static.lumi.new/ff/ff0e28b025c4939b79a561fc684dab73.webp", title: "White Sedan - Black Roof Wrap", brand: "Luxury" },
    { url: "https://static.lumi.new/32/326d128dc64c0316595e6905e1997e8a.webp", title: "Audi - Satin Black Chrome Delete", brand: "Audi" },
    { url: "https://static.lumi.new/5b/5bde8a60bc95c8d032447e94cfa13ddc.webp", title: "Satin Black PPF Install", brand: "BMW" },
    { url: "https://static.lumi.new/aa/aadcadaca470a35df5de761425455e6f.webp", title: "Maserati Ghibli - White PPF Install", brand: "Maserati" },
    { url: "https://static.lumi.new/ff/ff9ce8ba7bae51f4631031e8c8c79cf6.webp", title: "Cybertruck - Custom Wrap", brand: "Cybertruck" },
    { url: "https://static.lumi.new/ec/ecf111d72314812c63caf042d438cde9.webp", title: "RZR - Off-Road PPF Protection", brand: "RZR" },
    { url: "https://static.lumi.new/1c/1c91ccf9aae3ffd30b7ec6818e7c1be9.webp", title: "Corvette - Blue Custom Wrap", brand: "Tesla" },
    { url: "https://static.lumi.new/5f/5fcb5f57d5c454cef199e33f58fc17c1.webp", title: "Corvette - White Performance Wrap", brand: "Maserati" },
    { url: "https://static.lumi.new/e6/e659d3e6a65094e8bda2aa02e59b8175.webp", title: "Mini Cooper - Geometric Pattern Wrap", brand: "Mini Cooper" },
    { url: "https://static.lumi.new/0d/0d8d3eafcad007f5a368582452a5a93d.webp", title: "Corvette Stingray - Red Racing Wrap", brand: "Corvette" },
    { url: "https://static.lumi.new/7f/7f7b55612b8b88ba7b1592c6a2cb9079.webp", title: "Porsche - Satin Grey Wrap", brand: "Porsche" },
    { url: "https://static.lumi.new/ae/aea200193d3c54e006500507f3468b16.webp", title: "Porsche Taycan - Matte Black Wrap", brand: "Porsche" },
    { url: "https://static.lumi.new/bd/bd706036c250478f6f43df7a151a30b2.webp", title: "Rolls Royce - Black Luxury Wrap", brand: "Rolls Royce" },
    { url: "https://static.lumi.new/6f/6fc5d28178e4f43a3665066e1c1a03cc.webp", title: "Ferrari - Red Performance Wrap in Shop", brand: "Luxury" },
    { url: "https://static.lumi.new/2a/2ac0d58ffedc960e351096946076ecca.webp", title: "Sport Bike - Orange Custom Wrap", brand: "Motorcycle" },
    { url: "https://static.lumi.new/d2/d22708d2b86f21fc45d1cf0052ffdae7.webp", title: "Cybertruck - Lime Green Wrap", brand: "Cybertruck" },
    { url: "https://static.lumi.new/3e/3e56c84e7bb95d77c505cf1ac8429a3c.webp", title: "Cybertruck - Gold Wrap", brand: "Cybertruck" },
    { url: "https://static.lumi.new/87/87c4692b5eb8c1f6abf7fdc3009a1c48.webp", title: "Ferrari - Wild Custom Graphics Wrap", brand: "Luxury" },
    { url: "https://static.lumi.new/b1/b13b6596d94a2449da41f47cf79d4b10.webp", title: "Cybertruck - Shop Installation", brand: "Cybertruck" },
    { url: "https://static.lumi.new/26/26d11d729cfb0597825542998906bc30.webp", title: "Mini Cooper & Custom Car - Shop Work", brand: "Mini Cooper" },
    { url: "https://static.lumi.new/81/811f626179d0437b24d2e7fefc5d3e91.webp", title: "Chevy Silverado - Purple Chameleon Wrap", brand: "Chevy" },
    { url: "https://static.lumi.new/df/df3506c6c1c23f0406da6d85c07c3951.webp", title: "RZR Graphics - Custom Wrap Parts", brand: "RZR" },
    { url: "https://static.lumi.new/19/19d83cb0361216b06d5a9b8acd0991ff.webp", title: "Gas Pump - Stars & Stripes Wrap", brand: "Commercial Wraps" },
    { url: "https://static.lumi.new/0c/0ca02c4d405f6ee54a295361e59a08e3.webp", title: "Tesla Model Y - Blue Matte Wrap at Shop", brand: "Tesla" },
    { url: "https://static.lumi.new/87/879541e5573098031ac2b43cc164edc9.webp", title: "Mercedes C-Class - White at Shop Entrance", brand: "Mercedes" },
    { url: "https://static.lumi.new/53/53fabada986c98cb080bb41c6104db3a.webp", title: "Mercedes C63 - Matte Blue Wrap in Shop", brand: "Mercedes" },
    { url: "https://static.lumi.new/32/3243709b69db5b259fae981188ff2173.webp", title: "Chevy Pickup - Classic Blue Matte Wrap", brand: "Chevy" },
    { url: "https://static.lumi.new/1f/1ffc5017d8f563809b45ef5207ed4270.webp", title: "Professional Wrap Installation Process", brand: "Luxury" }
  ]

  const testimonials = [
    {
      name: "Satisfied Customer",
      vehicle: "10 Year Old Truck - Satin Black Wrap",
      text: "Highly recommend, they deserve 10 out of 10 stars. Mr Wrap and the whole crew are amazing! From the very first visit to the completed job; they were friendly, knowledgeable, timely, and communicated throughout. The Satin Black wrap makes my 10 yr old truck look new again! Seriously, great work y'all 🤘",
      rating: 5,
      image: "https://static.lumi.new/e6/e6a36eb91fcf8ba1c7b3a58e96503f47.webp"
    },
    {
      name: "BMW X5 M50i Owner",
      vehicle: "BMW X5 M50i - PPF & Full Ceramic Tint",
      text: "The Mr. Wrap team did an impeccable job installing a PPF and Full Ceramic tint on my BMW X5 M50i. I was guaranteed the lowest price in San Diego and they did not disappoint in price and quality. The results were astounding in comparison to other quotes and shops I visited. From turn-in to retrieval they were detail oriented and extremely knowledgeable about their preparation materials, installation quality and aftercare for long lasting results. They did not lead me astray. Not only did they remain flexible and attentive to my requests, they continued to entertain any thoughts or concerns that arose throughout the entire process. Highly recommend the Mr. Wrap team for anyone looking for affordable quality expertise and years of experience wrapped (no pun intended) into one location!",
      rating: 5,
      image: "https://static.lumi.new/04/04d2303152a94c2d0426a20f8de35776.webp"
    },
    {
      name: "Maserati Ghibli Owner",
      vehicle: "2023 Maserati Ghibli - Matte Frozen Icy White PPF",
      text: "Absolutely blown away by the transformation of my 2023 Maserati Ghibli. I brought my Maserati Ghibli to the team for a full color change in Inozetek's Matte Frozen Icy White PPF, and the results are nothing short of incredible. The finish is smooth, clean, and looks like a true factory matte paint job - except better because it's protected. The attention to detail on this install is unreal. They also wrapped the roof, mirrors, and interior trim in a carbon PPF, which gave the car the perfect contrast and a more aggressive, high end look. The carbon work is flawless - no seams, no lifting, and perfectly aligned edges all around. On top of that, they protected the headlights with clear PPF, so the whole front end is fully shielded from rock chips and UV fading which was a concern of mine. From start to finish, the communication was clear, the quality was top tier, and the craftsmanship speaks for itself. I felt the shop truly treats every car like it's their own. I couldn't be happier with how it turned out, and I wouldn't trust my vehicle with anyone else. Highly recommend to anyone who cares about premium materials, real expertise, and perfection level results.",
      rating: 5,
      image: "https://static.lumi.new/aa/aadcadaca470a35df5de761425455e6f.webp"
    }
  ]

  // Navigation Component
  const Navigation = () => (
    <nav className="fixed top-0 left-0 right-0 bg-black/95 backdrop-blur-md z-50 border-b border-cyan-500/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigateTo("home")}
          >
            <img 
              src="https://static.lumi.new/71/7132523da35951c352e5739adcc9dffa.webp" 
              alt="Mr. Wrap Logo" 
              className="h-16 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button onClick={() => navigateTo("home")} className={`text-gray-300 hover:text-cyan-400 transition ${currentPage === "home" ? "text-cyan-400 font-semibold" : ""}`}>
              Home
            </button>
            <div 
              className="relative"
              onMouseEnter={() => {
                if (closeDropdownTimeout) {
                  clearTimeout(closeDropdownTimeout)
                  setCloseDropdownTimeout(null)
                }
                setServicesDropdownOpen(true)
              }}
              onMouseLeave={() => {
                const timeout = setTimeout(() => {
                  setServicesDropdownOpen(false)
                }, 250)
                setCloseDropdownTimeout(timeout)
              }}
            >
              <button onClick={() => navigateTo("services")} className={`text-gray-300 hover:text-cyan-400 transition ${currentPage === "services" ? "text-cyan-400 font-semibold" : ""}`}>
                Services
              </button>
              {servicesDropdownOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-64 bg-gray-900 border border-cyan-500/20 rounded-lg shadow-xl py-2 z-50"
                  onMouseEnter={() => setServicesDropdownOpen(true)}
                  onMouseLeave={() => setServicesDropdownOpen(false)}
                >
                  <button onClick={() => navigate("/paint-protection-film")} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-black/50 transition">
                    Paint Protection Film (PPF)
                  </button>
                  <button onClick={() => navigate("/car-wrap-san-diego")} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-black/50 transition">
                    Vinyl Wrap
                  </button>
                  <button onClick={() => navigate("/commercial-fleet-wraps")} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-black/50 transition">
                    Fleet & Commercial Wraps
                  </button>
                  <button onClick={() => navigate("/window-tinting")} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-black/50 transition">
                    Window Tinting
                  </button>
                  <button onClick={() => navigate("/chrome-delete")} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-black/50 transition">
                    Chrome Delete
                  </button>
                  <button onClick={() => navigate("/caliper-painting")} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-black/50 transition">
                    Caliper Painting
                  </button>
                  <button onClick={() => navigate("/headlight-taillight-tint")} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-black/50 transition">
                    Headlight & Taillight Tint
                  </button>
                  <button onClick={() => navigate("/interior-wrap-trim")} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-black/50 transition">
                    Interior Wrap & Trim
                  </button>
                  <button onClick={() => navigate("/racing-stripes-graphics")} className="block w-full text-left px-4 py-2 text-gray-300 hover:text-cyan-400 hover:bg-black/50 transition">
                    Racing Stripes & Graphics
                  </button>
                </div>
              )}
            </div>
            <button onClick={() => navigateTo("gallery")} className={`text-gray-300 hover:text-cyan-400 transition ${currentPage === "gallery" ? "text-cyan-400 font-semibold" : ""}`}>
              Gallery
            </button>
            <button onClick={() => navigateTo("about")} className={`text-gray-300 hover:text-cyan-400 transition ${currentPage === "about" ? "text-cyan-400 font-semibold" : ""}`}>
              About
            </button>
            <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-5 py-2 rounded-lg font-semibold transition">
              <Phone size={18} />
              Call
            </a>
            <a href="sms:858-282-8119" className="flex items-center gap-2 bg-cyan-500/20 hover:bg-cyan-500/30 border-2 border-cyan-500 text-cyan-400 px-5 py-2 rounded-lg font-semibold transition">
              <MessageCircle size={18} />
              Text
            </a>
            <button 
              onClick={() => navigateTo("contact")}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg font-semibold transition"
            >
              <Calendar size={18} />
              Book Now
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-4">
            <button onClick={() => navigateTo("home")} className="block w-full text-left text-gray-300 hover:text-cyan-400 transition py-2">
              Home
            </button>
            
            {/* Mobile Services Accordion */}
            <div>
              <button 
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)} 
                className="block w-full text-left text-gray-300 hover:text-cyan-400 transition py-2 flex items-center justify-between"
              >
                Services
                <ChevronRight size={18} className={`transform transition-transform ${mobileServicesOpen ? 'rotate-90' : ''}`} />
              </button>
              {mobileServicesOpen && (
                <div className="pl-4 pt-2 space-y-2">
                  <button onClick={() => { navigate("/paint-protection-film"); setMobileMenuOpen(false); setMobileServicesOpen(false); }} className="block w-full text-left text-gray-400 hover:text-cyan-400 transition py-2 text-sm">
                    Paint Protection Film (PPF)
                  </button>
                  <button onClick={() => { navigate("/car-wrap-san-diego"); setMobileMenuOpen(false); setMobileServicesOpen(false); }} className="block w-full text-left text-gray-400 hover:text-cyan-400 transition py-2 text-sm">
                    Vinyl Wrap
                  </button>
                  <button onClick={() => { navigate("/commercial-fleet-wraps"); setMobileMenuOpen(false); setMobileServicesOpen(false); }} className="block w-full text-left text-gray-400 hover:text-cyan-400 transition py-2 text-sm">
                    Fleet & Commercial Wraps
                  </button>
                  <button onClick={() => { navigate("/window-tinting"); setMobileMenuOpen(false); setMobileServicesOpen(false); }} className="block w-full text-left text-gray-400 hover:text-cyan-400 transition py-2 text-sm">
                    Window Tinting
                  </button>
                  <button onClick={() => { navigate("/chrome-delete"); setMobileMenuOpen(false); setMobileServicesOpen(false); }} className="block w-full text-left text-gray-400 hover:text-cyan-400 transition py-2 text-sm">
                    Chrome Delete
                  </button>
                  <button onClick={() => { navigate("/caliper-painting"); setMobileMenuOpen(false); setMobileServicesOpen(false); }} className="block w-full text-left text-gray-400 hover:text-cyan-400 transition py-2 text-sm">
                    Caliper Painting
                  </button>
                  <button onClick={() => { navigate("/headlight-taillight-tint"); setMobileMenuOpen(false); setMobileServicesOpen(false); }} className="block w-full text-left text-gray-400 hover:text-cyan-400 transition py-2 text-sm">
                    Headlight & Taillight Tint
                  </button>
                  <button onClick={() => { navigate("/interior-wrap-trim"); setMobileMenuOpen(false); setMobileServicesOpen(false); }} className="block w-full text-left text-gray-400 hover:text-cyan-400 transition py-2 text-sm">
                    Interior Wrap & Trim
                  </button>
                  <button onClick={() => { navigate("/racing-stripes-graphics"); setMobileMenuOpen(false); setMobileServicesOpen(false); }} className="block w-full text-left text-gray-400 hover:text-cyan-400 transition py-2 text-sm">
                    Racing Stripes & Graphics
                  </button>
                </div>
              )}
            </div>
            
            <button onClick={() => navigateTo("gallery")} className="block w-full text-left text-gray-300 hover:text-cyan-400 transition py-2">
              Gallery
            </button>
            <button onClick={() => navigateTo("about")} className="block w-full text-left text-gray-300 hover:text-cyan-400 transition py-2">
              About
            </button>
            <a href="tel:858-282-8119" className="block bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-2 rounded-lg font-semibold transition text-center">
              Call Now
            </a>
            <a href="sms:858-282-8119" className="block bg-cyan-500/20 border-2 border-cyan-500 hover:bg-cyan-500/30 text-cyan-400 px-6 py-2 rounded-lg font-semibold transition text-center">
              Text Us
            </a>
            <button 
              onClick={() => navigateTo("contact")}
              className="block w-full bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-semibold transition text-center"
            >
              Schedule Appointment
            </button>
          </div>
        )}
      </div>
    </nav>
  )

  // Home Page
  const HomePage = () => (
    <>
      <CanonicalTag url="/" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": "https://autowrapsandiego.com/#localbusiness",
        "name": "Mr. Wrap San Diego",
        "description": "Professional paint protection film (PPF), vehicle wraps, ceramic coating, window tinting, chrome delete, and caliper painting in San Diego and North County.",
        "url": "https://autowrapsandiego.com/",
        "telephone": "+1-858-282-8119",
        "email": "mrwrapmatt@gmail.com",
        "image": "https://static.lumi.new/71/7132523da35951c352e5739adcc9dffa.webp",
        "priceRange": "$$-$$$",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "2244 South Santa Fe Avenue, Suite A7",
          "addressLocality": "Vista",
          "addressRegion": "CA",
          "postalCode": "92084",
          "addressCountry": "US"
        },
        "areaServed": [
          "San Diego", "Vista", "Oceanside", "Carlsbad", "San Marcos", "Escondido",
          "Encinitas", "Poway", "Del Mar", "La Jolla", "Rancho Santa Fe", "North County San Diego", "San Diego County"
        ],
        "openingHoursSpecification": [
          { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "08:00", "closes": "18:00" },
          { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "09:00", "closes": "16:00" }
        ]
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": "https://autowrapsandiego.com/#website",
        "name": "Mr. Wrap San Diego",
        "url": "https://autowrapsandiego.com/",
        "publisher": { "@id": "https://autowrapsandiego.com/#localbusiness" }
      }) }} />
      {/* Hero Section with Floating Cars */}
      <section className="relative min-h-screen flex items-center justify-center bg-black pt-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        </div>
        
        {/* Floating Car Images - Behind Logo */}
        <div className="absolute inset-0 pointer-events-none z-0 flex items-center justify-center">
          <img 
            src="https://images.dealer.com/ddc/vehicles/2023/BMW/i7/Sedan/perspective/front-left/2023_76.png"
            alt="Luxury vehicle PPF protection San Diego"
            className="w-[500px] opacity-20"
            style={{animation: "float 6s ease-in-out infinite"}}
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="relative inline-block mb-8 animate-fade-in">
            <img 
              src="https://static.lumi.new/71/7132523da35951c352e5739adcc9dffa.webp" 
              alt="Mr Wrap San Diego PPF Installation Logo - Paint Protection Film Experts" 
              className="w-80 h-auto mx-auto drop-shadow-2xl"
            />
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white animate-fade-in-up">
            Car Wraps, Paint Protection Film (PPF), Chrome Delete & Window Tint in San Diego
          </h1>
          <h2 className="text-xl sm:text-2xl mb-8 text-gray-300 max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: "0.2s"}}>
            Serving San Diego, Vista, Carlsbad, Encinitas, Oceanside and North County.
          </h2>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-fade-in-up" style={{animationDelay: "0.4s"}}>
            <button 
              onClick={() => navigateTo("contact")}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-10 rounded-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-2"
            >
              <Calendar size={20} />
              Build My Package
            </button>
            <a 
              href="tel:858-282-8119"
              className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-4 px-10 rounded-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-2"
            >
              <Phone size={20} />
              Book a 10-Min Consult
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto text-white">
            <div className="animate-fade-in-up" style={{animationDelay: "0.6s"}}>
              <div className="text-4xl font-bold text-cyan-400 mb-2">13+</div>
              <div className="text-gray-300">Years of Experience</div>
            </div>
            <div className="animate-fade-in-up" style={{animationDelay: "0.7s"}}>
              <div className="text-4xl font-bold text-cyan-400 mb-2">Vista</div>
              <div className="text-gray-300">Shop Installs</div>
            </div>
            <div className="animate-fade-in-up" style={{animationDelay: "0.8s"}}>
              <div className="text-4xl font-bold text-cyan-400 mb-2">Care</div>
              <div className="text-gray-300">On Every Job</div>
            </div>
            <div className="animate-fade-in-up" style={{animationDelay: "0.9s"}}>
              <div className="text-4xl font-bold text-cyan-400 mb-2">5★</div>
              <div className="text-gray-300">Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Standards Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Our Standards</h2>
          <div className="grid md:grid-cols-2 gap-6 text-gray-300">
            <div className="flex items-start gap-3">
              <Check className="text-cyan-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <span className="font-semibold text-white">Professional Paint Protection Film Installers</span> — Experienced technicians focused on clean, careful installation that supports applicable manufacturer warranty coverage.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="text-cyan-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <span className="font-semibold text-white">Premium Films</span> — We install professional-grade PPF and wrap materials selected for durability, clarity, and the vehicle’s intended use.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="text-cyan-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <span className="font-semibold text-white">Full Disassembly When Required</span> — Professional bumper removal and reassembly for seamless edge-to-edge coverage.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="text-cyan-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <span className="font-semibold text-white">Precision Installation</span> — We select bulk film or precision-pattern methods based on the vehicle and panel, with wrapped or tucked edges where appropriate.
              </div>
            </div>
            <div className="flex items-start gap-3 md:col-span-2 md:justify-center md:max-w-xl md:mx-auto">
              <Check className="text-cyan-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <span className="font-semibold text-white">Built to Last</span> — Premium self-healing films with manufacturer warranty terms that vary by product, designed to help maintain a clean finish for years.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-black border-t border-cyan-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-white">Why Choose Mr. Wrap for San Diego PPF?</h2>
            <p className="text-xl text-gray-400">North County's Premier Paint Protection Film Specialists</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl shadow-lg border border-cyan-500/20 hover:border-cyan-500/40 transition">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Professional Installers</h3>
              <p className="text-gray-300 leading-relaxed">
                Experienced installers focused on clean prep, careful fitment, and workmanship that supports applicable film warranties.
              </p>
            </div>

            <div className="bg-gray-900 p-8 rounded-xl shadow-lg border border-cyan-500/20 hover:border-cyan-500/40 transition">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">13 Years of Experience</h3>
              <p className="text-gray-300 leading-relaxed">
                13 years of professional installation experience protecting vehicles throughout San Diego County.
              </p>
            </div>

            <div className="bg-gray-900 p-8 rounded-xl shadow-lg border border-cyan-500/20 hover:border-cyan-500/40 transition">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Premium Films Only</h3>
              <p className="text-gray-300 leading-relaxed">
                We install premium paint protection film and professional-grade materials selected for the vehicle, coverage, and finish you want.
              </p>
            </div>

            <div className="bg-gray-900 p-8 rounded-xl shadow-lg border border-cyan-500/20 hover:border-cyan-500/40 transition">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Tesla PPF Specialists</h3>
              <p className="text-gray-300 leading-relaxed">
                Expert installers specializing in Tesla Model 3, Model Y, Model S, and Model X with precision-cut patterns.
              </p>
            </div>

            <div className="bg-gray-900 p-8 rounded-xl shadow-lg border border-cyan-500/20 hover:border-cyan-500/40 transition">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Zap className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Full Disassembly Experts</h3>
              <p className="text-gray-300 leading-relaxed">
                Professional bumper removal and reassembly for seamless edge-to-edge coverage on every installation.
              </p>
            </div>

            <div className="bg-gray-900 p-8 rounded-xl shadow-lg border border-cyan-500/20 hover:border-cyan-500/40 transition">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <MapPin className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">North County San Diego</h3>
              <p className="text-gray-300 leading-relaxed">
                Conveniently located in Vista, serving Carlsbad, Oceanside, Encinitas, and all San Diego communities.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigateTo("contact")}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center gap-2"
            >
              <Calendar size={20} />
              Schedule Your Free Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Luxury, Exotic & New Vehicle PPF Specialists */}
      <section className="py-20 bg-black border-t border-cyan-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-cyan-400 text-sm font-semibold uppercase tracking-[0.2em] mb-4">Exotic &amp; Luxury Vehicle Protection</p>
            <h2 className="text-4xl sm:text-5xl font-light uppercase tracking-wide mb-6 text-white leading-tight">
              Luxury, Exotic &amp; New Vehicle PPF Specialists in San Diego
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Based in Vista, Mr. Wrap serves North County and greater San Diego with premium{" "}
              <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:text-cyan-300 underline">paint protection film</a>,{" "}
              <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:text-cyan-300 underline">colored PPF</a>,{" "}
              <a href="/vinyl-wraps" className="text-cyan-400 hover:text-cyan-300 underline">vinyl wraps</a>, and complete vehicle customization.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-4 text-gray-300 leading-relaxed text-lg">
              <p>
                Owner and lead installer Matt brings 13 years of professional automotive wrap and PPF installation experience throughout the San Diego area, including extensive hands-on work with exotic cars, supercars, luxury vehicles, performance cars, premium SUVs, and electric vehicles.
              </p>
              <p>
                Matt's installation experience spans an exceptionally broad range of makes, including Ferrari, Lamborghini, McLaren, Porsche, Rolls-Royce, Bentley, Aston Martin, Mercedes-AMG, BMW M, Corvette, Tesla, Range Rover, and GMC Hummer EV — genuine examples of hands-on work, not hypothetical categories.
              </p>
              <p>
                Whether you're protecting a new-vehicle purchase, a performance car, or a premium SUV, we bring the same attention to detail and careful preparation to every{" "}
                <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:text-cyan-300 underline">colored PPF</a> or full-body installation, serving exotic car owners and new-vehicle buyers throughout Vista and San Diego County.
              </p>
            </div>

            <div className="bg-gray-900 rounded-2xl p-8 border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-6 text-white">Build a Complete Protection Package</h3>
              <ul className="space-y-3 text-gray-300 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Full-body clear gloss PPF</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Matte &amp; stealth PPF</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Colored PPF</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Premium vinyl color-change wraps</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Ceramic window tint &amp; windshield protection film</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Custom wheel powder coating &amp; caliper painting</span>
                </li>
              </ul>
              <p className="text-gray-400 text-sm mb-6">
                Combine any of these into one personalized package for complete new-vehicle protection and customization in Vista and across San Diego County.
              </p>
              <button
                onClick={() => navigateTo("contact")}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2"
              >
                <Calendar size={20} />
                Get a Custom Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Real Luxury & Exotic Vehicle Installations */}
      <section className="py-20 bg-gray-900 border-t border-cyan-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-white">Real Luxury &amp; Exotic Vehicle Installations by Mr Wrap</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              From full-body clear paint protection film on Corvettes and a Rolls-Royce, to colored PPF that changes a vehicle's color while protecting the paint underneath, to vinyl color-change wraps on a Porsche Taycan and Lamborghini models, explore real installations completed by Mr Wrap's team. Based in Vista, we serve vehicle owners throughout North County and greater San Diego.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Project 1 — Orange Corvette C8 */}
            <div className="bg-black rounded-2xl overflow-hidden border border-cyan-500/20 flex flex-col">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://static.lumi.new/a1/a1f4416915bc26c24ef56a5aefe3dbfc.webp"
                  alt="Orange Chevrolet Corvette C8 with full-body clear paint protection film"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={1200}
                  height={900}
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wide mb-2">Full-Body Clear PPF</p>
                <h3 className="text-xl font-bold text-white mb-2">Chevrolet Corvette C8 — Full-Body Clear PPF</h3>
                <p className="text-gray-400 text-sm">Full-body clear <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:text-cyan-300 underline">paint protection film</a> installed on this Corvette C8, helping protect the vehicle's exterior paint from everyday road exposure.</p>
              </div>
            </div>

            {/* Project 2 — Red Corvette C8 */}
            <div className="bg-black rounded-2xl overflow-hidden border border-cyan-500/20 flex flex-col">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://static.lumi.new/6d/6db6d2c701ba286c034891a6fd3a77dd.webp"
                  alt="Red Chevrolet Corvette C8 with full-body clear PPF, custom graphics, and ceramic windshield tint"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={1200}
                  height={900}
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wide mb-2">Clear PPF + Graphics + Ceramic Tint</p>
                <h3 className="text-xl font-bold text-white mb-2">Chevrolet Corvette C8 — Clear PPF, Graphics &amp; Ceramic Windshield Tint</h3>
                <p className="text-gray-400 text-sm">Full-body clear paint protection film paired with custom graphics and ceramic windshield tint on this <a href="/corvette-ppf-san-diego" className="text-cyan-400 hover:text-cyan-300 underline">Corvette C8</a>.</p>
              </div>
            </div>

            {/* Project 3 — Porsche Taycan */}
            <div className="bg-black rounded-2xl overflow-hidden border border-cyan-500/20 flex flex-col">
              <div className="grid grid-cols-2 gap-1">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://static.lumi.new/8e/8e4dce06736b5bb85023e6533325df91.webp"
                    alt="Porsche Taycan with vinyl color-change wrap, front view"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={600}
                    height={450}
                  />
                </div>
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://static.lumi.new/75/75d8f2ed5fe887c116007e0c6ed95918.webp"
                    alt="Porsche Taycan with vinyl color-change wrap, side profile"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={600}
                    height={450}
                  />
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wide mb-2">Vinyl Wrap + Ceramic Coating</p>
                <h3 className="text-xl font-bold text-white mb-2">Porsche Taycan — Vinyl Color-Change Wrap &amp; Ceramic Coating</h3>
                <p className="text-gray-400 text-sm">Full <a href="/vinyl-wraps" className="text-cyan-400 hover:text-cyan-300 underline">vinyl color-change wrap</a> combined with ceramic coating, giving this Taycan a new exterior appearance along with an added protective layer. Unlike paint protection film, vinyl wrap is focused on appearance customization rather than impact protection.</p>
              </div>
            </div>

            {/* Project 4 — Rolls-Royce */}
            <div className="bg-black rounded-2xl overflow-hidden border border-cyan-500/20 flex flex-col">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://static.lumi.new/6b/6b0d985f85ee57eb35d902d79386ad4d.webp"
                  alt="Rolls-Royce with full-body clear PPF outside Mr Wrap in Vista"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={1200}
                  height={900}
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wide mb-2">Full-Body Clear PPF</p>
                <h3 className="text-xl font-bold text-white mb-2">Rolls-Royce — Full-Body Clear PPF</h3>
                <p className="text-gray-400 text-sm">Complete full-body clear <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:text-cyan-300 underline">paint protection film</a> installation to protect this Rolls-Royce's factory paint finish.</p>
              </div>
            </div>

            {/* Project 5 — BMW 7 Series */}
            <div className="bg-black rounded-2xl overflow-hidden border border-cyan-500/20 flex flex-col">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://static.lumi.new/18/18f9ea64e3bfe6780ae734f6f603a9d4.webp"
                  alt="BMW 7 Series with full-body colored paint protection film"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={1200}
                  height={900}
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wide mb-2">Colored PPF + Tint + Headlight Protection</p>
                <h3 className="text-xl font-bold text-white mb-2">BMW 7 Series — Colored PPF, Tint &amp; Headlight Protection</h3>
                <p className="text-gray-400 text-sm">Full-body <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:text-cyan-300 underline">colored PPF</a> combined with window tint and headlight protection film, changing this BMW 7 Series' appearance while adding a protective layer over the factory paint.</p>
              </div>
            </div>

            {/* Project 6 — White Lamborghini */}
            <div className="bg-black rounded-2xl overflow-hidden border border-cyan-500/20 flex flex-col">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://static.lumi.new/f9/f91481cd9df0c935c21521a7fa91a7f9.webp"
                  alt="White Lamborghini with vinyl color-change wrap"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={1200}
                  height={900}
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wide mb-2">Vinyl Color-Change Wrap</p>
                <h3 className="text-xl font-bold text-white mb-2">Lamborghini — Vinyl Color-Change Wrap</h3>
                <p className="text-gray-400 text-sm">Premium <a href="/vinyl-wraps" className="text-cyan-400 hover:text-cyan-300 underline">vinyl color-change wrap</a> for a complete exterior transformation. Vinyl wrap changes a vehicle's appearance and is not the same as paint protection film.</p>
              </div>
            </div>
          </div>

          {/* Project 7 — Matte Black Lamborghini Huracán, centered */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
            <div className="bg-black rounded-2xl overflow-hidden border border-cyan-500/20 flex flex-col lg:col-start-2">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://static.lumi.new/1a/1a2224b48c118d6adf2718c243542235.webp"
                  alt="Matte black Lamborghini Huracan with full-body colored paint protection film"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={1200}
                  height={900}
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wide mb-2">Full-Body Colored PPF</p>
                <h3 className="text-xl font-bold text-white mb-2">Lamborghini Huracán — Full-Body Colored PPF</h3>
                <p className="text-gray-400 text-sm">Full-body <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:text-cyan-300 underline">colored PPF</a> installation that changes this Lamborghini Huracán's color while preserving the factory paint with a durable protective layer.</p>
              </div>
            </div>
          </div>

          {/* Additional project photos */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-white text-center mb-2">More From Our Shop</h3>
            <p className="text-gray-400 text-center max-w-2xl mx-auto mb-8">Additional project photos from Mr Wrap's Vista shop.</p>
            <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <div className="bg-black rounded-2xl overflow-hidden border border-cyan-500/20">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://static.lumi.new/2c/2c1562f5df4af9c0b6b0b7aadb47a37f.webp"
                    alt="Dark gray Lamborghini Aventador photographed at Mr Wrap in Vista"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={1200}
                    height={900}
                  />
                </div>
                <div className="p-4">
                  <p className="text-gray-400 text-sm">Additional project photo from Mr Wrap's shop in Vista.</p>
                </div>
              </div>
              <div className="bg-black rounded-2xl overflow-hidden border border-cyan-500/20">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://static.lumi.new/8d/8d37062a288627090fb0679cd05f2914.webp"
                    alt="Behind-the-scenes installation photo of a white Lamborghini at Mr Wrap in Vista"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={1200}
                    height={900}
                  />
                </div>
                <div className="p-4">
                  <p className="text-gray-400 text-sm">Behind-the-scenes look at installation work in our Vista shop.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigateTo("contact")}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-10 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center gap-2"
            >
              <Calendar size={20} />
              Request Your Custom Vehicle Quote
            </button>
          </div>
        </div>
      </section>

      {/* Services Grid - Featured 4 Only */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-white">Professional PPF and Auto Film Services in San Diego</h2>
            <p className="text-xl text-gray-400">Professional paint protection film, ceramic window tinting, and vehicle wraps</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[services[0], services[1], services[2], services[4]].map((service, index) => (
              <div key={index} className="group bg-gray-900 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={service.image}
                    alt={`${service.title} San Diego - Professional auto film service`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>
                </div>
                <div className="p-8">
                  <h3 className="text-3xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">{service.title}</h3>
                  <p className="text-gray-400 mb-6 text-lg">{service.description}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Check className="text-cyan-400 flex-shrink-0" size={18} />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <button 
                    onClick={() => navigateTo("services")}
                    className="mt-6 text-cyan-400 font-semibold hover:text-cyan-300 transition flex items-center gap-2"
                  >
                    Learn More <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigateTo("contact")}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center gap-2"
            >
              <Calendar size={20} />
              Schedule Your Appointment
            </button>
          </div>
        </div>
      </section>



      {/* PPF Coverage Options Carousel - Compact Version */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">Paint Protection Film Coverage Options</h2>
            <p className="text-xl text-gray-400">Choose the level of protection that fits your needs</p>
          </div>

          <div className="relative max-w-3xl mx-auto">
            {/* Carousel Container */}
            <div 
              className="overflow-hidden"
              onTouchStart={(e) => setPPFTouchStart(e.targetTouches[0].clientX)}
              onTouchMove={(e) => setPPFTouchEnd(e.targetTouches[0].clientX)}
              onTouchEnd={() => {
                if (ppfTouchStart - ppfTouchEnd > 75) {
                  setCurrentPPFSlide((prev) => Math.min(prev + 1, 2))
                }
                if (ppfTouchStart - ppfTouchEnd < -75) {
                  setCurrentPPFSlide((prev) => Math.max(prev - 1, 0))
                }
              }}
            >
              <div 
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentPPFSlide * 100}%)` }}
              >
                {/* Front Bumper */}
                <div className="w-full flex-shrink-0 px-4">
                  <div className="bg-black p-8 border-2 border-dashed border-gray-700 rounded-2xl hover:border-gray-600 transition">
                    <div className="mb-8 relative">
                      <img 
                        src="https://static.lumi.new/d4/d4ca2f7fd1ae1cc4e627ca968217a42f.webp"
                        alt="Front End PPF Coverage"
                        className="w-full h-auto"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-transparent pointer-events-none"></div>
                    </div>
                    <h3 className="text-3xl font-bold mb-4 text-white">Front Bumper</h3>
                    <p className="text-gray-400 mb-4 text-lg leading-relaxed">
                      The best way to protect the front bumper of your car is with paint protection film.
                    </p>
                    <p className="text-cyan-400 text-sm mb-8 italic">
                      Best for: Budget-conscious drivers and daily commuters
                    </p>
                    <button
                      onClick={() => navigateTo("contact")}
                      className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-4 px-12 rounded-lg transition-all text-lg"
                    >
                      Learn More
                    </button>
                  </div>
                </div>

                {/* Full Front Kit */}
                <div className="w-full flex-shrink-0 px-4">
                  <div className="bg-black p-8 border-2 border-dashed border-gray-700 rounded-2xl hover:border-gray-600 transition">
                    <div className="mb-8 relative">
                      <img 
                        src="https://static.lumi.new/17/170d5798a33ff5c5083331aadc363d9e.webp"
                        alt="Full Front Kit PPF Coverage"
                        className="w-full h-auto"
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/30 via-cyan-500/10 to-transparent pointer-events-none"></div>
                    </div>
                    <h3 className="text-3xl font-bold mb-4 text-white">Full Front Kit</h3>
                    <p className="text-gray-400 mb-4 text-lg leading-relaxed">
                      Covers the entire hood, the full fenders, the front bumper, and headlights.
                    </p>
                    <p className="text-cyan-400 text-sm mb-8 italic">
                      Best for: Highway drivers and new vehicle owners
                    </p>
                    <button
                      onClick={() => navigateTo("contact")}
                      className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-4 px-12 rounded-lg transition-all text-lg"
                    >
                      Learn More
                    </button>
                  </div>
                </div>

                {/* Full Car */}
                <div className="w-full flex-shrink-0 px-4">
                  <div className="bg-black p-8 border-2 border-dashed border-gray-700 rounded-2xl hover:border-gray-600 transition">
                    <div className="mb-8 relative">
                      <img 
                        src="https://static.lumi.new/5a/5a229510120d87611812a469fa145abe.webp"
                        alt="Full Car PPF Coverage"
                        className="w-full h-auto"
                      />
                      <div className="absolute inset-0 bg-cyan-500/20 pointer-events-none"></div>
                    </div>
                    <h3 className="text-3xl font-bold mb-4 text-white">Full Car</h3>
                    <p className="text-gray-400 mb-4 text-lg leading-relaxed">
                      Complete protection for every painted surface, preserving showroom condition and maximizing resale value. Covers the entire vehicle including hood, roof, doors, fenders, bumpers, and all panels.
                    </p>
                    <p className="text-cyan-400 text-sm mb-8 italic">
                      Best for: Luxury vehicles and maximum protection
                    </p>
                    <button
                      onClick={() => navigateTo("contact")}
                      className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-4 px-12 rounded-lg transition-all text-lg"
                    >
                      Learn More
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setCurrentPPFSlide((prev) => Math.max(prev - 1, 0));
              }}
              disabled={currentPPFSlide === 0}
              className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-cyan-500 hover:bg-cyan-600 text-white p-3 rounded-full shadow-lg transition ${
                currentPPFSlide === 0 ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
              }`}
              aria-label="Previous coverage option"
            >
              <ChevronRight size={24} className="rotate-180" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setCurrentPPFSlide((prev) => Math.min(prev + 1, 2));
              }}
              disabled={currentPPFSlide === 2}
              className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-cyan-500 hover:bg-cyan-600 text-white p-3 rounded-full shadow-lg transition ${
                currentPPFSlide === 2 ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
              }`}
              aria-label="Next coverage option"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {[0, 1, 2].map((index) => (
              <button
                type="button"
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentPPFSlide(index);
                }}
                className={`w-3 h-3 rounded-full transition ${
                  currentPPFSlide === index ? 'bg-cyan-500 w-8' : 'bg-gray-600'
                }`}
                aria-label={`Go to coverage option ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews Carousel */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">San Diego PPF Reviews</h2>
            <p className="text-xl text-gray-400">Real customer testimonials from our San Diego clients</p>
          </div>

          <div className="relative">
            {/* Carousel Container */}
            <div 
              className="overflow-hidden"
              onTouchStart={(e) => setTouchStart(e.targetTouches[0].clientX)}
              onTouchMove={(e) => setTouchEnd(e.targetTouches[0].clientX)}
              onTouchEnd={() => {
                if (touchStart - touchEnd > 75) {
                  // Swipe left - next slide
                  setCurrentReviewSlide((prev) => Math.min(prev + 1, testimonials.length - 1))
                }
                if (touchStart - touchEnd < -75) {
                  // Swipe right - previous slide
                  setCurrentReviewSlide((prev) => Math.max(prev - 1, 0))
                }
              }}
            >
              <div 
                className="flex transition-transform duration-500 ease-out"
                style={{ 
                  transform: `translateX(-${currentReviewSlide * 100}%)` 
                }}
              >
                {testimonials.map((testimonial, index) => (
                  <div 
                    key={index} 
                    className="w-full md:w-1/2 flex-shrink-0 px-4"
                  >
                    <div className="bg-black rounded-2xl p-8 shadow-xl border border-cyan-500/20 h-full">
                      <div className="flex gap-1 mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="text-cyan-400 fill-cyan-400" size={20} />
                        ))}
                      </div>
                      <p className="text-gray-300 mb-6 italic leading-relaxed line-clamp-6">"{testimonial.text}"</p>
                      <div className="flex items-center gap-4">
                        <img 
                          src={testimonial.image}
                          alt={`${testimonial.vehicle} PPF San Diego customer review`}
                          className="w-16 h-16 rounded-full object-cover border-2 border-cyan-500"
                        />
                        <div>
                          <p className="font-bold text-white">{testimonial.name}</p>
                          <p className="text-sm text-gray-400">{testimonial.vehicle}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setCurrentReviewSlide((prev) => Math.max(prev - 1, 0));
              }}
              disabled={currentReviewSlide === 0}
              className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-cyan-500 hover:bg-cyan-600 text-white p-3 rounded-full shadow-lg transition ${
                currentReviewSlide === 0 ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
              }`}
              aria-label="Previous review"
            >
              <ChevronRight size={24} className="rotate-180" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setCurrentReviewSlide((prev) => Math.min(prev + 1, testimonials.length - 1));
              }}
              disabled={currentReviewSlide === testimonials.length - 1}
              className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-cyan-500 hover:bg-cyan-600 text-white p-3 rounded-full shadow-lg transition ${
                currentReviewSlide === testimonials.length - 1 ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
              }`}
              aria-label="Next review"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                type="button"
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentReviewSlide(index);
                }}
                className={`w-3 h-3 rounded-full transition ${
                  currentReviewSlide === index ? 'bg-cyan-500 w-8' : 'bg-gray-600'
                }`}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>



      {/* Featured Work Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-white">Featured Work</h2>
            <p className="text-xl text-gray-400">Real customer transformations from our Vista shop.</p>
          </div>

          {/* Before/After Sliders Row */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Slot 1 — Tesla PPF */}
            <div className="bg-gray-900 rounded-2xl overflow-hidden border border-cyan-500/20 p-4">
              <BeforeAfterSlider
                beforeImage="placeholder:tesla-ppf-before"
                afterImage="https://static.lumi.new/3b/3b849b078f4a32e1663976d79744117d.webp"
                beforeLabel="Before PPF"
                afterLabel="After PPF"
                beforeAlt="Tesla Model 3 before PPF installation"
                afterAlt="Tesla Model 3 after full PPF installation"
                projectTitle="Tesla Model 3 — Full Colored PPF"
                serviceBadge="Full PPF + Ceramic Tint"
              />
              <p className="text-gray-300 text-sm mt-4 px-1">Complete premium color change PPF with 20% ceramic window tint for North County commuter.</p>
            </div>

            {/* Slot 2 — BMW Color Change */}
            <div className="bg-gray-900 rounded-2xl overflow-hidden border border-cyan-500/20 p-4">
              <BeforeAfterSlider
                beforeImage="placeholder:bmw-wrap-before"
                afterImage="https://static.lumi.new/c7/c78cb81a446620a625d8454e3919a7cd.webp"
                beforeLabel="Before Wrap"
                afterLabel="After Wrap"
                beforeAlt="BMW M3 before color change wrap"
                afterAlt="BMW M3 after Inozetek Laguna Blue wrap"
                projectTitle="BMW M3 — Full Color Change Wrap"
                serviceBadge="Vinyl Wrap + Chrome Delete"
              />
              <p className="text-gray-300 text-sm mt-4 px-1">Inozetek Laguna Blue Vinyl Wrap with gloss black chrome delete and caliper painting.</p>
            </div>

            {/* Slot 3 — PPF Protection Story */}
            <div className="bg-gray-900 rounded-2xl overflow-hidden border border-cyan-500/20 p-4">
              <BeforeAfterSlider
                beforeImage="placeholder:range-rover-ppf-before"
                afterImage="https://static.lumi.new/3f/3f55d3a9c3a7cb148dc864f4654c6428.webp"
                beforeLabel="Damage Caught"
                afterLabel="PPF Protected"
                beforeAlt="Range Rover paint damage caught by PPF"
                afterAlt="Range Rover after satin PPF installation"
                projectTitle="Range Rover — Satin PPF Success Story"
                serviceBadge="Full Body Satin PPF"
              />
              <p className="text-gray-300 text-sm mt-4 px-1">PPF absorbed road damage, was cleaned and repaired — the paint underneath was pristine.</p>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => navigateTo("gallery")}
              className="bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 text-cyan-400 font-semibold py-2.5 px-8 rounded-lg transition"
            >
              View Full Gallery →
            </button>
          </div>
        </div>
      </section>

      {/* How Pricing Works */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6 text-white">How Pricing Works</h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Every vehicle is different, and every project is unique. Paint condition, panel complexity, material selection, and coverage area all affect final pricing.
            </p>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              That's why we require a brief consultation before quoting. We'll assess your specific vehicle, discuss your goals, and provide an accurate, transparent price with no surprises.
            </p>
            <p className="text-lg text-gray-400">
              Book a 10-minute consult by phone, or fill out our contact form to build a custom package for your vehicle.
            </p>
          </div>

          <div className="text-center mt-8">
            <h3 className="text-2xl font-bold mb-4 text-white">Starting Prices</h3>
            <div className="text-base text-gray-300 leading-relaxed max-w-2xl mx-auto text-left">
              <div className="grid md:grid-cols-2 gap-6 mb-4">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-white mb-2">Paint Protection Film (PPF)</p>
                    <ul className="space-y-1 pl-4">
                      <li>• Front Bumper — starting at $499+</li>
                      <li>• Partial Front — starting at $1,299+</li>
                      <li>• Full Front — starting at $1,899+</li>
                      <li>• Full Vehicle Gloss — starting at $4,499+</li>
                      <li>• Matte / Stealth — starting at $5,499+</li>
                      <li>• Colored PPF — starting at $5,999+</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-white mb-2">Vinyl Wraps</p>
                    <ul className="space-y-1 pl-4">
                      <li>• Full Color Change — starting at $2,299+</li>
                      <li>• Premium / Custom — starting at $3,500+</li>
                    </ul>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-white mb-2">Window Tint</p>
                    <ul className="space-y-1 pl-4">
                      <li>• Standard — starting at $299+</li>
                      <li>• Ceramic — starting at $499+</li>
                      <li>• Commercial Tints — Custom pricing available</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-white mb-2">Fleet & Commercial</p>
                    <ul className="space-y-1 pl-4">
                      <li>• Custom pricing available</li>
                    </ul>
                  </div>
                </div>
              </div>
              <p className="text-center text-gray-400 text-sm mt-6 italic">Final pricing varies by vehicle, coverage, and materials.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Services in San Diego */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">Popular Services in San Diego</h2>
            <p className="text-xl text-gray-400">Explore our most requested auto protection services</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            <a 
              href="/tesla-ppf-san-diego" 
              className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition-all group"
            >
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Tesla PPF San Diego</h3>
              <p className="text-gray-300 text-sm">Premium paint protection for Tesla Model 3, Y, S, and X</p>
            </a>
            <a 
              href="/paint-protection-film-san-diego" 
              className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition-all group"
            >
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
              <p className="text-gray-300 text-sm">Self-healing PPF for all makes and models</p>
            </a>
            <a 
              href="/car-wrap-san-diego" 
              className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition-all group"
            >
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Car Wrap San Diego</h3>
              <p className="text-gray-300 text-sm">Full color change and custom vinyl wraps</p>
            </a>
            <a 
              href="/chrome-delete-san-diego" 
              className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition-all group"
            >
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Chrome Delete San Diego</h3>
              <p className="text-gray-300 text-sm">Modern blackout styling and trim wraps</p>
            </a>
            <a 
              href="/window-tint-san-diego" 
              className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition-all group"
            >
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Window Tint San Diego</h3>
              <p className="text-gray-300 text-sm">Premium ceramic window tinting services</p>
            </a>
          </div>
        </div>
      </section>

      {/* Paint Protection Film Experts in San Diego */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">Paint Protection Film Experts in San Diego</h2>
            <p className="text-xl text-gray-400">Professional PPF installation serving San Diego County and North County communities</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            <a 
              href="/paint-protection-film-san-diego" 
              className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition-all group"
            >
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
              <p className="text-gray-300 text-sm">Premium self-healing PPF for all vehicle makes and models</p>
            </a>
            <a 
              href="/tesla-ppf-san-diego" 
              className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition-all group"
            >
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Tesla PPF San Diego</h3>
              <p className="text-gray-300 text-sm">Expert Tesla Model 3, Y, S, and X protection specialists</p>
            </a>
            <a 
              href="/ppf-vista" 
              className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition-all group"
            >
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">PPF Vista</h3>
              <p className="text-gray-300 text-sm">Local Vista paint protection film installation experts</p>
            </a>
            <a 
              href="/ppf-carlsbad" 
              className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition-all group"
            >
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">PPF Carlsbad</h3>
              <p className="text-gray-300 text-sm">Luxury vehicle PPF serving Carlsbad communities</p>
            </a>
            <a 
              href="/ppf-oceanside" 
              className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition-all group"
            >
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">PPF Oceanside</h3>
              <p className="text-gray-300 text-sm">Coastal vehicle protection for Oceanside and Camp Pendleton</p>
            </a>
            <a 
              href="/corvette-ppf-san-diego" 
              className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition-all group"
            >
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Corvette PPF San Diego</h3>
              <p className="text-gray-300 text-sm">Paint protection film for C8 Stingray, Z06, E-Ray, and C7 Corvettes</p>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-white">
            Ready to Protect Your Investment?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Start with a free consultation to discuss your vehicle and goals
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigateTo("contact")}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-10 rounded-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-2"
            >
              <Calendar size={20} />
              Build My Package
            </button>
            <a 
              href="tel:858-282-8119"
              className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-10 rounded-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-2"
            >
              <Phone size={20} />
              Book a 10-Min Consult
            </a>
          </div>
        </div>
      </section>
    </>
  )

  // ... keep existing code for other pages

  // Services Page
  const ServicesPage = () => (
    <div className="pt-20">
      <CanonicalTag url="/services" />
      {/* Services Hero */}
      <section className="bg-gradient-to-br from-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Professional Auto Film Services</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Comprehensive vehicle protection and customization using premium materials
          </p>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {services.map((service, index) => {
            const serviceId = service.title.toLowerCase().replace(/ /g, "-").replace(/&/g, "")
            return (
              <div 
                key={index} 
                id={serviceId}
                className={`flex flex-col ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} gap-12 items-center scroll-mt-24`}
              >
                <div className="flex-1">
                  <h2 className="text-4xl font-bold mb-4 text-white">{service.title}</h2>
                  <p className="text-xl text-gray-400 mb-6">{service.description}</p>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <Check className="text-cyan-400 flex-shrink-0" size={24} />
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-4">
                    <button
                      onClick={() => navigateTo("contact")}
                      className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-lg transition-all inline-flex items-center gap-2"
                    >
                      <Calendar size={18} />
                      Get Quote
                    </button>
                    <button
                      onClick={() => navigateTo(`service-${serviceId}`)}
                      className="bg-cyan-500/20 border-2 border-cyan-500 hover:bg-cyan-500/30 text-cyan-400 font-bold py-3 px-8 rounded-lg transition-all inline-flex items-center gap-2"
                    >
                      Learn More <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
                <div className="flex-1">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="rounded-2xl shadow-2xl w-full"
                  />
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* Service CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Not Sure Which Service You Need?</h2>
          <p className="text-xl text-white/90 mb-8">
            Our experts are here to help you choose the perfect solution for your vehicle
          </p>
          <button
            onClick={() => navigateTo("contact")}
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center gap-2"
          >
            <Phone size={20} />
            Get Expert Advice
          </button>
        </div>
      </section>
    </div>
  )

  // Gallery Page
  const GalleryPage = () => {
    const filteredGallery = selectedBrand === "all" ? gallery : gallery.filter(item => item.brand === selectedBrand)
    
    return (
      <div className="pt-20">
        <CanonicalTag url="/gallery" />
        {/* Gallery Hero */}
        <section className="bg-gradient-to-br from-black to-gray-900 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl font-bold mb-6">Our Work</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Browse through our portfolio of completed projects showcasing quality craftsmanship
            </p>
          </div>
        </section>

        {/* Filter Buttons */}
        <section className="py-8 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => setSelectedBrand("all")}
                className={`px-6 py-2 rounded-lg font-semibold transition ${selectedBrand === "all" ? "bg-cyan-500 text-white" : "bg-black text-gray-300 hover:bg-gray-800"}`}
              >
                All Vehicles
              </button>
              {carBrands.map((brand, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedBrand(brand.name)}
                  className={`px-6 py-2 rounded-lg font-semibold transition ${selectedBrand === brand.name ? "bg-cyan-500 text-white" : "bg-black text-gray-300 hover:bg-gray-800"}`}
                >
                  {brand.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Browse by Vehicle Brand - Moved from Homepage */}
        <section className="py-12 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Browse by Vehicle Brand</h2>
              <p className="text-lg text-gray-400">Click any brand to filter the gallery</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {carBrands.map((brand, index) => (
                <div 
                  key={index} 
                  onClick={() => setSelectedBrand(brand.name)}
                  className="group relative overflow-hidden rounded-2xl shadow-xl cursor-pointer transform hover:scale-105 transition-all"
                >
                  <img 
                    src={brand.image}
                    alt={`${brand.name} PPF San Diego - Paint protection film installation`}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{brand.name}</h3>
                    <div className="flex items-center text-cyan-400 font-semibold">
                      View Gallery <ChevronRight size={18} className="ml-1" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredGallery.map((item, index) => (
                <div key={index} className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                  <img 
                    src={item.url}
                    alt={item.title}
                    className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery CTA */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6 text-white">Want Your Vehicle Featured Here?</h2>
            <p className="text-xl text-gray-400 mb-8">
              Let's create something amazing together
            </p>
            <button
              onClick={() => navigateTo("contact")}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center gap-2"
            >
              <Calendar size={20} />
              Start Your Project
            </button>
          </div>
        </section>
      </div>
    )
  }

  // About Page
  const AboutPage = () => (
    <div className="pt-20">
      <CanonicalTag url="/about" />
      {/* About Hero */}
      <section className="bg-gradient-to-br from-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">About Mr. Wrap</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            San Diego's trusted auto film specialists with 13 years of professional installation experience
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-white">San Diego's Auto Film Specialists</h2>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  With 13 years of professional installation experience in the auto film industry, Mr. Wrap focuses on the craft of vehicle protection. Our work spans paint protection film, window tinting, vehicle wraps, and chrome delete services.
                </p>
                <p>
                  We install professional-grade materials from established manufacturers such as 3M and Avery Dennison, selected to match the vehicle and the protection or finish you want.
                </p>
                <p>
                  Whether you're a Tesla owner seeking ultimate paint protection, a luxury car enthusiast wanting to preserve your investment, or simply someone who values their vehicle, we deliver professional results every time.
                </p>
                <p>
                  Based in Vista and proudly serving all of San Diego County, Mr. Wrap combines technical precision with exceptional customer service.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://static.lumi.new/ed/ed6800d20cbd80acd0ecba228d496619.png"
                alt="Professional installation"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-white">Our Values</h2>
            <p className="text-xl text-gray-400">What drives us to deliver excellence every day</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black p-8 rounded-xl shadow-lg border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white text-center">Quality</h3>
              <p className="text-gray-300 text-center">
                We never compromise on materials or workmanship. Every project receives the same meticulous attention to detail.
              </p>
            </div>

            <div className="bg-black p-8 rounded-xl shadow-lg border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto border border-cyan-500">
                <Users className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white text-center">Customer First</h3>
              <p className="text-gray-300 text-center">
                Your satisfaction is our priority. We listen to your vision and deliver results that exceed expectations.
              </p>
            </div>

            <div className="bg-black p-8 rounded-xl shadow-lg border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white text-center">Expertise</h3>
              <p className="text-gray-300 text-center">
                13 years of professional installation experience means we can help guide your project with practical, shop-based knowledge.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-white">What Our Clients Say</h2>
            <p className="text-xl text-gray-400">Real feedback from satisfied customers</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-900 p-8 rounded-xl shadow-lg border border-cyan-500/20">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="text-cyan-400 fill-cyan-400" size={20} />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">
                  "{testimonial.text}"
                </p>
                <p className="font-bold text-white">{testimonial.name}</p>
                <p className="text-sm text-gray-400">{testimonial.vehicle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )



  // Footer Component
  const Footer = () => (
    <footer className="bg-black text-white py-12 border-t border-cyan-500/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-5 gap-8 mb-8">
          {/* Brand */}
          <div>
            <img 
              src="https://static.lumi.new/e5/e5d5ae9b6f07a9f87888a440c86d2bb3.webp" 
              alt="Mr. Wrap Logo" 
              className="h-20 w-auto mb-4"
            />
            <p className="text-gray-400">
              San Diego auto film specialists with 13 years of professional installation experience
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold mb-4 text-cyan-400">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => navigateTo("home")} className="text-gray-400 hover:text-cyan-400 transition">
                  Home
                </button>
              </li>
              <li>
                <button onClick={() => navigateTo("services")} className="text-gray-400 hover:text-cyan-400 transition">
                  Services
                </button>
              </li>
              <li>
                <button onClick={() => navigateTo("gallery")} className="text-gray-400 hover:text-cyan-400 transition">
                  Gallery
                </button>
              </li>
              <li>
                <button onClick={() => navigateTo("about")} className="text-gray-400 hover:text-cyan-400 transition">
                  About
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold mb-4 text-cyan-400">Services</h4>
            <ul className="space-y-2 text-gray-400">
              <li>Paint Protection Film</li>
              <li>Window Tinting</li>
              <li>Vehicle Wraps</li>
              <li>Chrome Delete</li>
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h4 className="font-bold mb-4 text-cyan-400">Service Areas</h4>
            <ul className="space-y-2">
              <li>
                <a href="https://autowrapsandiego.com/north-county-vinyl-wraps" className="text-gray-400 hover:text-cyan-400 transition">
                  North County Vinyl Wraps
                </a>
              </li>
              <li>
                <a href="https://autowrapsandiego.com/ppf-vista" className="text-gray-400 hover:text-cyan-400 transition">
                  Vista PPF
                </a>
              </li>
              <li>
                <a href="https://autowrapsandiego.com/ppf-carlsbad" className="text-gray-400 hover:text-cyan-400 transition">
                  Carlsbad PPF
                </a>
              </li>
              <li>
                <a href="https://autowrapsandiego.com/ppf-encinitas" className="text-gray-400 hover:text-cyan-400 transition">
                  Encinitas PPF
                </a>
              </li>
              <li>
                <a href="https://autowrapsandiego.com/ppf-rancho-santa-fe" className="text-gray-400 hover:text-cyan-400 transition">
                  Rancho Santa Fe PPF
                </a>
              </li>
              <li>
                <a href="https://autowrapsandiego.com/ppf-san-diego" className="text-gray-400 hover:text-cyan-400 transition">
                  San Diego PPF
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold mb-4 text-cyan-400">Contact</h4>
            <ul className="space-y-2 text-gray-400">
              <li>2244 South Santa Fe Avenue Suite A7</li>
              <li>Vista, CA 92084</li>
              <li>
                <a href="tel:858-282-8119" className="hover:text-cyan-400 transition">
                  858-282-8119
                </a>
              </li>
              <li>
                <a href="mailto:mrwrapmatt@gmail.com" className="hover:text-cyan-400 transition">
                  mrwrapmatt@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            © 2025 Mr. Wrap. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <button onClick={() => navigateTo("privacy")} className="text-gray-400 hover:text-cyan-400 transition">
              Privacy Policy
            </button>
            <button onClick={() => navigateTo("terms")} className="text-gray-400 hover:text-cyan-400 transition">
              Terms of Service
            </button>
          </div>
        </div>
      </div>
    </footer>
  )

  // Privacy Policy Page
  const PrivacyPage = () => (
    <div className="min-h-screen bg-black pt-20">
      <CanonicalTag url="/privacy" />
      <div className="max-w-4xl mx-auto px-4 py-16">
        <button
          onClick={() => navigateTo("home")}
          className="mb-8 text-cyan-400 hover:text-cyan-300 font-medium flex items-center gap-2"
        >
          ← Back to Home
        </button>
        <h1 className="text-4xl font-bold mb-8 text-white">Privacy Policy</h1>
        <div className="prose prose-lg max-w-none text-gray-300 space-y-6">
          <p><strong>Effective Date:</strong> January 2025</p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-white">1. Information We Collect</h2>
          <p>We collect information you provide directly to us when you submit a contact form, including your name, phone number, email address, and project details.</p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-white">2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Respond to your inquiries about our auto film services</li>
            <li>Provide quotes and service information</li>
            <li>Communicate with you about our services</li>
            <li>Improve our website and services</li>
          </ul>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-white">3. Information Sharing</h2>
          <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only with service providers who assist us in operating our business.</p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-white">4. Data Security</h2>
          <p>We implement reasonable security measures to protect your personal information from unauthorized access, alteration, or disclosure.</p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-white">5. Your Rights</h2>
          <p>You have the right to access, correct, or delete your personal information. Contact us at the information provided on our website.</p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-white">6. Contact Us</h2>
          <p>If you have questions about this Privacy Policy, please contact us at 858-282-8119 or mrwrapmatt@gmail.com.</p>
        </div>
      </div>
    </div>
  )

  // Terms Page
  const TermsPage = () => (
    <div className="min-h-screen bg-black pt-20">
      <CanonicalTag url="/terms" />
      <div className="max-w-4xl mx-auto px-4 py-16">
        <button
          onClick={() => navigateTo("home")}
          className="mb-8 text-cyan-400 hover:text-cyan-300 font-medium flex items-center gap-2"
        >
          ← Back to Home
        </button>
        <h1 className="text-4xl font-bold mb-8 text-white">Terms of Service</h1>
        <div className="prose prose-lg max-w-none text-gray-300 space-y-6">
          <p><strong>Effective Date:</strong> January 2025</p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-white">1. Services</h2>
          <p>Mr. Wrap provides professional paint protection film, window tinting, vehicle wraps, and chrome delete services.</p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-white">2. Quotes and Pricing</h2>
          <p>All quotes are estimates based on the information provided. Final pricing may vary based on the actual condition of the vehicle and scope of work required.</p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-white">3. Warranty</h2>
          <p>We stand behind our work with manufacturer warranties on materials and workmanship warranties on installation. Specific warranty terms will be provided with your quote.</p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-white">4. Payment Terms</h2>
          <p>Payment terms will be outlined in your service agreement. We accept various forms of payment including cash, credit cards, and checks.</p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-white">5. Cancellation</h2>
          <p>Cancellation policies vary by service type and will be outlined in your service agreement. Deposits may be non-refundable.</p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-white">6. Contact</h2>
          <p>For questions about these terms, contact us at 858-282-8119 or mrwrapmatt@gmail.com.</p>
        </div>
      </div>
    </div>
  )

  // Dedicated Service Pages
  const ServiceDetailPage = ({ serviceId }: { serviceId: string }) => {
    const serviceContent: Record<string, {
      title: string
      subtitle: string
      description: string[]
      features: string[]
      image: string
      benefits: { icon: typeof Shield, title: string, description: string }[]
    }> = {
      "paint-protection-film": {
        title: "Paint Protection Film (PPF)",
        subtitle: "Ultimate Protection with Premium Paint Protection Film",
        description: [
          "Paint Protection Film is the most advanced solution for protecting your vehicle's paint from rock chips, scratches, and environmental damage. Our premium PPF features revolutionary self-healing technology that repairs minor scratches and swirl marks with heat.",
          "We select bulk film or precision-pattern methods based on the vehicle and panel, then wrap or tuck edges where appropriate. The crystal-clear finish is designed to stay discreet while protecting factory paint and helping preserve resale value.",
          "Premium PPF is typically backed by a manufacturer warranty that varies by film. Specific coverage is confirmed with your quote."
        ],
        features: ["Manufacturer Warranty", "Self-Healing Technology", "Crystal Clear Finish", "Precision Installation", "Stain Resistant", "Hydrophobic Coating"],
        image: "https://static.lumi.new/56/56cd8dc39eb01f582efecfa70381ab9b.webp",
        benefits: [
          { icon: Shield, title: "Maximum Protection", description: "Guards against rock chips, scratches, and UV damage" },
          { icon: Sparkles, title: "Self-Healing", description: "Minor scratches disappear with heat application" },
          { icon: Award, title: "Manufacturer Warranty", description: "Warranty terms follow the applicable film and installation workmanship" }
        ]
      },
      "vinyl-wrap": {
        title: "Vinyl Vehicle Wraps",
        subtitle: "Transform Your Vehicle's Appearance",
        description: [
          "Vehicle wraps offer unlimited customization possibilities, from complete color changes to eye-catching custom graphics. Using premium 3M and Avery Dennison materials, we can transform your vehicle's appearance while protecting the original paint.",
          "Our expert installers have wrapped everything from exotic supercars to commercial fleets. Whether you want a sleek matte finish, glossy color change, or textured carbon fiber look, we deliver showroom-quality results.",
          "Wraps are fully removable and reversible, making them perfect for leases or if you want to change your vehicle's look in the future."
        ],
        features: ["3M & Avery Materials", "Full or Partial Wraps", "Removable & Reversible", "Custom Graphics", "Color Match Available", "5-Year Warranty"],
        image: "https://static.lumi.new/cc/cce71ddf6b6089aa064e68b2b617df57.webp",
        benefits: [
          { icon: Car, title: "Endless Options", description: "Thousands of colors and finishes to choose from" },
          { icon: Shield, title: "Paint Protection", description: "Protects original paint while transforming appearance" },
          { icon: Zap, title: "Reversible", description: "Remove anytime to restore factory finish" }
        ]
      },
      "commercial-wraps": {
        title: "Fleet & Commercial Wraps",
        subtitle: "Professional Branding for Your Business",
        description: [
          "Turn your business vehicles into mobile billboards with fleet wraps. Commercial vehicle wraps are one of the most cost-effective advertising investments, generating thousands of impressions daily while protecting your fleet.",
          "We specialize in fleet graphics for businesses of all sizes, from single vehicles to entire fleets. Our in-house design team creates consistent branding across all vehicles, ensuring your company maintains a professional image.",
          "Using durable materials designed for commercial use, our wraps withstand harsh conditions while maintaining vibrant colors. Volume discounts available for multi-vehicle projects."
        ],
        features: ["Custom Logo Design", "Durable Materials", "Consistent Branding", "Volume Discounts", "Fast Turnaround", "Professional Installation"],
        image: "https://static.lumi.new/59/59d04c24c2308e0666a9f0e83e60782e.webp",
        benefits: [
          { icon: Users, title: "Brand Visibility", description: "Generate thousands of daily impressions" },
          { icon: Shield, title: "Fleet Protection", description: "Protect company vehicles while advertising" },
          { icon: Award, title: "Volume Pricing", description: "Special rates for multiple vehicles" }
        ]
      },
      "window-tinting": {
        title: "Window Tinting",
        subtitle: "Premium Ceramic Tint Technology",
        description: [
          "Our premium ceramic window tint provides superior heat rejection, UV protection, and privacy while maintaining crystal-clear visibility. Unlike dyed films that fade over time, ceramic technology blocks infrared heat without interfering with electronics.",
          "We offer various shade levels to meet legal requirements while maximizing comfort and privacy. All installations include a lifetime warranty against fading, peeling, and bubbling.",
          "Ceramic tint keeps your vehicle's interior cooler, protects against harmful UV rays, and reduces glare for safer driving."
        ],
        features: ["99% UV Protection", "Lifetime Warranty", "Ceramic Technology", "No Signal Interference", "Heat Rejection", "Multiple Shades Available"],
        image: "https://static.lumi.new/c3/c3667fbc51078a3f60fd03b0ea2f8b3d.webp",
        benefits: [
          { icon: Shield, title: "UV Protection", description: "Blocks 99% of harmful UV rays" },
          { icon: Zap, title: "Heat Rejection", description: "Keeps interior significantly cooler" },
          { icon: Award, title: "Lifetime Warranty", description: "Coverage against fading and peeling" }
        ]
      },
      "chrome-delete": {
        title: "Chrome Delete",
        subtitle: "Modern Blacked-Out Aesthetic",
        description: [
          "Chrome delete is the perfect way to achieve a sleek, modern look by covering chrome trim with premium vinyl. This popular modification creates a cohesive, blacked-out appearance that's especially striking on luxury and performance vehicles.",
          "We carefully wrap all chrome elements including window trim, door handles, grilles, and badges. Available in gloss or matte black finishes to match your vision.",
          "The vinyl protects your original chrome, making the modification fully reversible. Quick installation with dramatic results."
        ],
        features: ["Gloss or Matte Finish", "Perfect Fit", "Preserves Original Chrome", "Quick Installation", "Reversible", "Premium Materials"],
        image: "https://static.lumi.new/9f/9f0bf5df0736ae5d758d7327eb952bff.webp",
        benefits: [
          { icon: Sparkles, title: "Modern Look", description: "Sleek blacked-out aesthetic" },
          { icon: Shield, title: "Protects Chrome", description: "Preserves original finish underneath" },
          { icon: Zap, title: "Quick Process", description: "Dramatic transformation in hours" }
        ]
      },
      "powder-coating": {
        title: "Caliper Painting & Powder Coating",
        subtitle: "Custom Brake Caliper Finishing",
        description: [
          "Professional brake caliper painting adds a custom touch to your wheels while showcasing attention to detail. We offer any color imaginable, from classic red to custom-matched hues that complement your vehicle's aesthetic.",
          "Our heat-resistant paint withstands extreme temperatures while maintaining vibrant color. We can also add custom decals including manufacturer logos or your own designs.",
          "In-house design services available to help you choose the perfect caliper color and create custom decals for a truly unique look."
        ],
        features: ["Any Color Available", "Custom Decals Printed", "In-House Design", "Heat-Resistant Paint", "Professional Prep", "Quick Turnaround"],
        image: "https://static.lumi.new/9e/9e0fad88cf9cb971449e53af9ab9e716.webp",
        benefits: [
          { icon: Sparkles, title: "Unlimited Colors", description: "Any color you can imagine" },
          { icon: Award, title: "Custom Decals", description: "Logos and custom designs available" },
          { icon: Shield, title: "Heat Resistant", description: "Withstands extreme brake temperatures" }
        ]
      },
      "headlight-taillight-tint": {
        title: "Headlight & Taillight Tint",
        subtitle: "Subtle Style Enhancement",
        description: [
          "Tinted headlights and taillights add a subtle yet striking modification that sets your vehicle apart. Using premium tint film, we create a smoked appearance while maintaining DOT-compliant light output.",
          "Our precision-cut installation ensures perfect fitment without bubbles or edges. The film also provides protection against rock chips and UV fading.",
          "Available in various darkness levels to match your style while keeping your vehicle street legal."
        ],
        features: ["DOT Compliant", "Multiple Shade Options", "Protection from Chips", "UV Resistant", "Perfect Fitment", "Professional Installation"],
        image: "https://static.lumi.new/9f/9f0bf5df0736ae5d758d7327eb952bff.webp",
        benefits: [
          { icon: Sparkles, title: "Enhanced Look", description: "Sophisticated smoked appearance" },
          { icon: Shield, title: "Lens Protection", description: "Guards against chips and scratches" },
          { icon: Award, title: "Street Legal", description: "DOT-compliant installations" }
        ]
      },
      "interior-wrap": {
        title: "Interior Wrap & Trim",
        subtitle: "Custom Interior Transformations",
        description: [
          "Interior wrapping allows you to completely customize your vehicle's cabin without permanent modifications. From carbon fiber dash accents to custom-colored trim pieces, we transform interiors while preserving original components.",
          "Popular applications include center console wraps, door panel accents, steering wheel trim, and dashboard elements. Perfect for personalizing luxury vehicles or refreshing worn interior pieces.",
          "All materials are selected for durability and UV resistance to ensure long-lasting beauty in daily use."
        ],
        features: ["Carbon Fiber Finishes", "Custom Colors", "Dash & Console", "Door Panels", "Removable", "UV Resistant"],
        image: "https://static.lumi.new/cc/cce71ddf6b6089aa064e68b2b617df57.webp",
        benefits: [
          { icon: Sparkles, title: "Total Customization", description: "Personalize every interior detail" },
          { icon: Shield, title: "Protects Original", description: "Preserves factory trim underneath" },
          { icon: Award, title: "Premium Materials", description: "High-quality, durable finishes" }
        ]
      },
      "stripe-packages": {
        title: "Racing Stripes & Graphics",
        subtitle: "Performance-Inspired Styling",
        description: [
          "Racing stripes and custom graphics add aggressive styling to any vehicle. From classic dual stripes to modern offset designs, we create the perfect look for your performance or muscle car.",
          "Our designers work with you to create custom stripe patterns, widths, and colors that complement your vehicle's lines. We use premium vinyl materials for durability and vibrant color.",
          "Popular options include hood stripes, full-length racing stripes, rocker panel graphics, and custom side graphics. Professional installation ensures perfect alignment and long-lasting adhesion."
        ],
        features: ["Custom Designs", "Any Color Combo", "Premium Vinyl", "Perfect Alignment", "Multiple Styles", "Graphics & Decals"],
        image: "https://static.lumi.new/cc/cce71ddf6b6089aa064e68b2b617df57.webp",
        benefits: [
          { icon: Zap, title: "Performance Look", description: "Aggressive racing-inspired styling" },
          { icon: Sparkles, title: "Fully Custom", description: "Unique designs tailored to your vision" },
          { icon: Award, title: "Perfect Alignment", description: "Professional precision installation" }
        ]
      }
    }

    const service = serviceContent[serviceId]
    if (!service) {
      navigateTo("services")
      return null
    }

    return (
      <div className="pt-20 min-h-screen bg-black">
        <CanonicalTag url={`/service-${serviceId}`} />
        {/* Hero */}
        <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => navigateTo("services")}
              className="mb-8 text-cyan-400 hover:text-cyan-300 font-medium flex items-center gap-2"
            >
              ← Back to Services
            </button>
            <div className="text-center">
              <h1 className="text-5xl font-bold mb-4">{service.title}</h1>
              <p className="text-2xl text-cyan-400 mb-8">{service.subtitle}</p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-20">
              <h2 className="text-3xl font-bold mb-6 text-white">Why Choose This Service?</h2>
              {service.description.map((paragraph, index) => (
                <p key={index} className="text-gray-300 mb-4 leading-relaxed text-lg">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Features Grid */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold mb-8 text-white text-center">Key Features</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {service.features.map((feature, index) => (
                  <div key={index} className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 flex items-center gap-3">
                    <Check className="text-cyan-400 flex-shrink-0" size={24} />
                    <span className="text-gray-300 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold mb-8 text-white text-center">Benefits</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {service.benefits.map((benefit, index) => (
                  <div key={index} className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20 text-center">
                    <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                      <benefit.icon className="text-cyan-400" size={32} />
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-white">{benefit.title}</h3>
                    <p className="text-gray-300">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-2xl p-12 text-center">
              <h2 className="text-4xl font-bold mb-4 text-white">Ready to Get Started?</h2>
              <p className="text-xl text-white/90 mb-8">
                Schedule your consultation today for a free quote
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigateTo("contact")}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2"
                >
                  <Calendar size={20} />
                  Book Appointment
                </button>
                <a
                  href="tel:858-282-8119"
                  className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2"
                >
                  <Phone size={20} />
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    )
  }

  // Vehicle Landing Pages Component
  const VehicleLandingPage = ({ vehicle: vehicleSlug }: { vehicle: string }) => {
    const vehicleContent: Record<string, {
      title: string
      subtitle: string
      description: string[]
      images: string[]
      faqs: { question: string, answer: string }[]
    }> = {
      "tesla-model-3": {
        title: "Tesla Model 3 PPF San Diego",
        subtitle: "Premium Paint Protection Film for Tesla Model 3 | Vista, CA",
        description: [
          "Protect your Tesla Model 3 with premium paint protection film in San Diego. Our expert installers specialize in Tesla vehicles, providing precision-cut PPF that maintains your Model 3's sleek design while offering superior protection against rock chips, scratches, and environmental damage.",
          "The Tesla Model 3's unique design requires specialized installation expertise. Our technicians understand Tesla paint characteristics and choose bulk or precision-pattern methods based on each panel, then wrap or tuck edges on the hood, fenders, bumpers, rocker panels, and door edges.",
          "Tesla Model 3 owners throughout San Diego County choose our shop for careful PPF installation. Premium self-healing film can help repair minor scratches with heat, keeping the finish looking cleaner for years."
        ],
        images: [
          "https://static.lumi.new/3b/3b849b078f4a32e1663976d79744117d.webp",
          "https://static.lumi.new/c5/c569c3f9888a7a4fed4a4801f7c73daa.webp",
          "https://static.lumi.new/75/75a2e5c024c25d5abc5286326335c10a.webp"
        ],
        faqs: [
          {
            question: "How much does Tesla Model 3 PPF cost in San Diego?",
            answer: "Full front PPF for Tesla Model 3 starts at $1,899, while full vehicle gloss coverage starts at $4,499. Exact pricing depends on coverage area, film selection, and installation complexity. Contact us for a quote based on your specific vehicle."
          },
          {
            question: "How long does Model 3 PPF installation take?",
            answer: "Full front installation takes 1-2 days, while complete vehicle coverage requires 3-4 days. We ensure proper curing time for optimal adhesion and finish quality."
          },
          {
            question: "Will PPF damage my Tesla's paint?",
            answer: "No. Premium paint protection film is designed specifically for automotive paint and removes cleanly without damage. In fact, it protects your factory paint underneath."
          },
          {
            question: "Does PPF affect Tesla's sensors or autopilot?",
            answer: "Premium PPF does not interfere with Tesla's sensors, cameras, or Autopilot functionality when properly installed around those components."
          },
          {
            question: "What warranty comes with Tesla Model 3 PPF?",
            answer: "Premium PPF typically includes a manufacturer warranty against yellowing, cracking, peeling, and hazing. Exact terms depend on the film selected, and we also stand behind our installation workmanship."
          }
        ]
      },
      "tesla-model-y": {
        title: "Tesla Model Y PPF San Diego",
        subtitle: "Premium Paint Protection for Tesla Model Y | Vista, CA",
        description: [
          "Safeguard your Tesla Model Y investment with professional paint protection film installation in San Diego. The Model Y's larger surface area and SUV design make it especially vulnerable to road debris, making PPF essential for maintaining resale value.",
          "Our Tesla Model Y PPF packages include comprehensive coverage options: full front (hood, fenders, bumper, mirrors, headlights), rocker panels, door edges, and full vehicle wraps. Each installation uses precision-cut patterns designed specifically for Model Y dimensions and curves.",
          "As a San Diego Tesla PPF shop, we understand the unique needs of Model Y owners. Our indoor installation bay and experienced installers focus on a clean finish that preserves your SUV's appearance while adding durable paint protection."
        ],
        images: [
          "https://static.lumi.new/db/db1a5a270d1e6dcb92ad91af0ba24fea.webp",
          "https://static.lumi.new/0ca02c4d405f6ee54a295361e59a08e3.webp",
          "https://static.lumi.new/c3/c3baa6df5270afa569a039a322e1876b.webp"
        ],
        faqs: [
          {
            question: "Is PPF necessary for Tesla Model Y?",
            answer: "Yes, especially in San Diego where highway driving is common. The Model Y's front fascia is low and wide, making it highly susceptible to rock chips and road debris damage."
          },
          {
            question: "What PPF coverage do you recommend for Model Y?",
            answer: "We recommend full front coverage at minimum (hood, fenders, bumper, mirrors). Many Model Y owners also add rocker panels and door edges for complete lower body protection."
          },
          {
            question: "Can I wrap my Model Y after PPF?",
            answer: "Yes! PPF should be installed first, then vinyl wrap can be applied over it for dual protection and color change capability."
          },
          {
            question: "How do I maintain PPF on my Model Y?",
            answer: "Hand wash or touchless car wash only. Avoid automatic brush washes. The hydrophobic coating makes cleaning easy, and minor scratches self-heal in sunlight."
          },
          {
            question: "Does PPF work with Model Y's white paint?",
            answer: "Absolutely. Premium PPF is crystal clear and specifically engineered to remain invisible on white and light-colored vehicles."
          }
        ]
      },
      "bmw": {
        title: "BMW PPF San Diego",
        subtitle: "Paint Protection Film for BMW M Series & X Series | Vista, CA",
        description: [
          "Preserve your BMW's ultimate driving machine status with precision paint protection film installation in San Diego. BMW's premium finishes and aerodynamic designs deserve the highest level of protection, and our premium PPF delivers exactly that.",
          "Whether you own an M3, M4, M5, X5, or any BMW model, our expert installers have extensive experience with BMW's unique paint characteristics, curves, and body lines. We use manufacturer-specific patterns that ensure seamless coverage without compromising your BMW's iconic design.",
          "San Diego's highways and coastal environment can be harsh on BMW paint. Rock chips, salt air, and UV exposure all threaten your vehicle's finish. Our PPF installations have protected hundreds of BMWs throughout San Diego County, maintaining their showroom appearance and resale value."
        ],
        images: [
          "https://static.lumi.new/c7/c78cb81a446620a625d8454e3919a7cd.webp",
          "https://static.lumi.new/6e/6ea5e690fc983ab1fb256192b4505cec.webp",
          "https://static.lumi.new/e3/e333d8989785826da29b7c8cfc256dca.webp"
        ],
        faqs: [
          {
            question: "Which BMW models do you install PPF on?",
            answer: "We install PPF on all BMW models including 3-Series, 5-Series, 7-Series, M3, M4, M5, X3, X5, X7, and specialty models. Custom patterns available for any BMW."
          },
          {
            question: "Is PPF recommended for BMW M Series?",
            answer: "Absolutely essential. M Series vehicles have aggressive front fascias that sit low to the ground, making them highly vulnerable to rock chips and road debris."
          },
          {
            question: "Does PPF affect BMW's warranty?",
            answer: "No. Professional PPF installation does not void BMW warranties. In fact, it helps maintain your vehicle in warranty-compliant condition."
          },
          {
            question: "Can you match BMW paint colors under PPF?",
            answer: "Premium PPF is completely clear and works perfectly with all BMW paint colors, from Alpine White to San Marino Blue to Individual colors."
          },
          {
            question: "How long does BMW PPF installation take?",
            answer: "Full front coverage takes 2 days, complete vehicle coverage takes 3-4 days depending on the model complexity."
          }
        ]
      },
      "mercedes": {
        title: "Mercedes-Benz PPF San Diego",
        subtitle: "Paint Protection for Mercedes G-Wagon, E-Class, S-Class | Vista, CA",
        description: [
          "Protect your Mercedes-Benz luxury vehicle with premium paint protection film in San Diego. Mercedes-Benz vehicles represent the pinnacle of automotive luxury, and our PPF installations ensure your investment maintains its pristine condition for years to come.",
          "From the iconic G-Wagon to the elegant E-Class and flagship S-Class, each Mercedes model receives specialized attention. We understand Mercedes paint systems and select bulk or precision-pattern methods based on the vehicle and panel so the film follows the body lines cleanly.",
          "San Diego's unique climate poses challenges for luxury vehicles. Our PPF installations have protected countless Mercedes vehicles from rock chips, scratches, UV fading, and environmental contaminants. Trust San Diego's premier Mercedes PPF specialist to preserve your vehicle's elegance."
        ],
        images: [
          "https://static.lumi.new/73/7321f92d0061134ffef642174e244cc9.webp",
          "https://static.lumi.new/b1/b16a6c9ee134b8fa61c6ebe3a2032a7a.webp",
          "https://static.lumi.new/53/53fabada986c98cb080bb41c6104db3a.webp"
        ],
        faqs: [
          {
            question: "Which Mercedes models need PPF most?",
            answer: "G-Wagon, AMG models, and any Mercedes with special paint finishes benefit most from PPF. The G-Wagon's boxy design and AMG's aggressive styling make them vulnerable to chips."
          },
          {
            question: "Does PPF work on Mercedes matte paint?",
            answer: "Yes! We offer specialized matte PPF that preserves matte finishes while providing protection. Regular gloss PPF would ruin a matte finish."
          },
          {
            question: "How much is full PPF for Mercedes G-Wagon?",
            answer: "Full vehicle PPF for the G-Wagon starts at $4,499, though the vehicle's size and angular body panels often require additional material and precise cutting that can increase the final price. We provide an exact quote after assessing your specific vehicle."
          },
          {
            question: "Will PPF protect Mercedes' special paint?",
            answer: "Yes. Premium PPF protects all Mercedes paint types including Designo finishes, metallic paints, and special order colors."
          },
          {
            question: "Can PPF be removed from Mercedes?",
            answer: "Yes. Professional PPF removal leaves no residue and does not damage Mercedes factory paint when removed by experts."
          }
        ]
      },
      "4runner": {
        title: "Toyota 4Runner PPF San Diego",
        subtitle: "Off-Road Paint Protection for 4Runner | Vista, CA",
        description: [
          "Protect your Toyota 4Runner with rugged paint protection film designed for off-road adventures in San Diego. Whether you tackle mountain trails or coastal highways, professional PPF keeps your 4Runner looking great despite the abuse of outdoor life.",
          "4Runner owners demand durability, and our PPF installations deliver. We focus on high-impact areas: full front bumper, hood, fenders, headlights, rocker panels, and door edges. Our thick, 8-mil film withstands rocks, branches, and trail debris better than any other protection.",
          "As San Diego's off-road PPF specialist, we understand 4Runner culture. Our installations protect your vehicle's value while letting you explore without worry. Thousands of local 4Runner owners trust us to keep their rigs protected from Anza-Borrego to the Laguna Mountains."
        ],
        images: [
          "https://static.lumi.new/ff/ff0e28b025c4939b79a561fc684dab73.webp",
          "https://static.lumi.new/50/506b46c1f21104cfd3e739fdc5066aa9.webp"
        ],
        faqs: [
          {
            question: "Is PPF worth it for off-road 4Runners?",
            answer: "Absolutely essential. Off-road use guarantees paint damage from rocks, branches, and trail debris. PPF pays for itself by preventing costly paint repairs."
          },
          {
            question: "Which areas of 4Runner need PPF most?",
            answer: "Full front bumper, hood, fenders, rocker panels, and door edges. These areas take the most abuse from trail debris and rock spray."
          },
          {
            question: "Can PPF handle off-road abuse?",
            answer: "Yes. Premium PPF is thick (8 mil) and designed to absorb impacts that would chip paint. It's self-healing for minor scratches."
          },
          {
            question: "How do I clean PPF after off-roading?",
            answer: "Rinse thoroughly to remove dirt and dust, then hand wash with pH-neutral soap. Avoid pressure washing directly on film edges."
          },
          {
            question: "Does PPF work on TRD Pro 4Runners?",
            answer: "Perfect for TRD Pro! We have specific patterns for all TRD Pro models and the film protects special paint colors without affecting appearance."
          }
        ]
      },
      "range-rover": {
        title: "Range Rover PPF San Diego",
        subtitle: "Luxury Paint Protection for Range Rover | Vista, CA",
        description: [
          "Preserve your Range Rover's commanding presence with premium paint protection film in San Diego. Range Rover represents British luxury at its finest, and our professional installations ensure your vehicle maintains its distinguished appearance despite daily driving challenges.",
          "Range Rover's large surface area and premium finishes require careful PPF installation. We work regularly on Land Rover vehicles and select bulk or precision-pattern methods for Sport, Velar, Evoque, and Defender models based on each panel and body line.",
          "San Diego Range Rover owners choose us for our attention to detail and understanding of luxury SUV needs. From full vehicle coverage to strategic front-end protection, we deliver flawless installations that complement your Range Rover's refined aesthetic while providing uncompromising protection."
        ],
        images: [
          "https://static.lumi.new/44/44db9d203660923d594de54b30995f0c.webp",
          "https://static.lumi.new/3f/3f55d3a9c3a7cb148dc864f4654c6428.webp",
          "https://static.lumi.new/0d/0d187c7d35932660a882bd902508abe9.webp"
        ],
        faqs: [
          {
            question: "Do you install PPF on all Range Rover models?",
            answer: "Yes, we install PPF on Range Rover Sport, Velar, Evoque, Defender, and classic Range Rover models with custom-cut patterns for each."
          },
          {
            question: "How much is Range Rover full vehicle PPF?",
            answer: "Full vehicle PPF for Range Rover starts at $4,499, though the model's larger surface area and body complexity can increase the final price depending on trim and size. We provide an exact quote after assessing your specific vehicle."
          },
          {
            question: "Will PPF affect Range Rover's sensors?",
            answer: "No. Premium PPF does not interfere with parking sensors, cameras, or driver assistance systems when properly installed."
          },
          {
            question: "Is PPF necessary for Range Rover?",
            answer: "Highly recommended. Range Rovers are significant investments with premium paint. PPF protects against chips, scratches, and environmental damage while maintaining resale value."
          },
          {
            question: "Can you do same-day Range Rover PPF?",
            answer: "Full installations require 3-4 days for proper prep, installation, and curing. Rushing compromises quality, and Range Rovers deserve the best."
          }
        ]
      }
    }

    const vehicleData = vehicleContent[vehicleSlug]
    if (!vehicleData) {
      navigateTo("home")
      return null
    }

    return (
      <div className="pt-20 min-h-screen bg-black">
        <CanonicalTag url={`/ppf-${vehicleSlug}`} />
        {/* Hero with Quote Form */}
        <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-5xl font-bold mb-4">{vehicleData.title}</h1>
                <h2 className="text-2xl text-cyan-400 mb-6">{vehicleData.subtitle}</h2>
                <div className="flex gap-4 mb-8">
                  <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                    <Phone size={18} />
                    858-282-8119
                  </a>
                  <button onClick={() => navigateTo("contact")} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                    <Calendar size={18} />
                    Book Now
                  </button>
                </div>
              </div>
              
              {/* Above-fold Quote Form */}
              <div className="bg-gray-900 rounded-2xl shadow-xl p-8 border border-cyan-500/20">
                <h3 className="text-2xl font-bold mb-6 text-white">Get Free Quote</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name *"
                    required
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none text-white"
                  />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone Number *"
                    required
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none text-white"
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email *"
                    required
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none text-white"
                  />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Additional details..."
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none text-white"
                  />
                  <button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-lg transition">
                    Get My Quote
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 mb-20">
              <div>
                <h2 className="text-3xl font-bold mb-6 text-white">Why Choose PPF for Your Vehicle?</h2>
                {vehicleData.description.map((paragraph, index) => (
                  <p key={index} className="text-gray-300 mb-4 leading-relaxed text-lg">
                    {paragraph}
                  </p>
                ))}
                <div className="mt-8">
                  <button onClick={() => navigateTo("services")} className="text-cyan-400 hover:text-cyan-300 font-semibold flex items-center gap-2">
                    View All Services <ChevronRight size={18} />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {vehicleData.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${vehicleData.title} installation example ${index + 1}`}
                    className="rounded-xl shadow-lg w-full h-48 object-cover"
                  />
                ))}
              </div>
            </div>

            {/* FAQs */}
            <div className="mb-20">
              <h2 className="text-4xl font-bold mb-12 text-white text-center">Frequently Asked Questions</h2>
              <div className="max-w-3xl mx-auto space-y-6">
                {vehicleData.faqs.map((faq, index) => (
                  <div key={index} className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
                    <h3 className="text-xl font-bold mb-3 text-white">{faq.question}</h3>
                    <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Links */}
            <div className="bg-gray-900 rounded-2xl p-8 mb-20">
              <h2 className="text-2xl font-bold mb-6 text-white text-center">Explore More PPF Options</h2>
              <div className="grid md:grid-cols-3 gap-4">
                <button onClick={() => navigateTo("ppf-tesla-model-3")} className="bg-black hover:bg-gray-800 text-cyan-400 px-6 py-3 rounded-lg transition text-center">
                  Tesla Model 3 PPF
                </button>
                <button onClick={() => navigateTo("ppf-bmw")} className="bg-black hover:bg-gray-800 text-cyan-400 px-6 py-3 rounded-lg transition text-center">
                  BMW PPF San Diego
                </button>
                <button onClick={() => navigateTo("ppf-san-diego")} className="bg-black hover:bg-gray-800 text-cyan-400 px-6 py-3 rounded-lg transition text-center">
                  PPF San Diego
                </button>
              </div>
            </div>

            {/* Related Services */}
            <div className="bg-gray-900 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold mb-6 text-white text-center">Related Services</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <a href="/paint-protection-film" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                  <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Paint Protection Film</h3>
                  <p className="text-gray-300">Premium PPF services and coverage options</p>
                </a>
                <a href="/window-tinting" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                  <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Window Tinting</h3>
                  <p className="text-gray-300">Ceramic tint for heat and UV protection</p>
                </a>
                <a href="/vinyl-wraps" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                  <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Vinyl Wraps</h3>
                  <p className="text-gray-300">Color change wraps and custom designs</p>
                </a>
              </div>
            </div>

            {/* Final CTA */}
            <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-2xl p-12 text-center">
              <h2 className="text-4xl font-bold mb-4 text-white">Ready to Protect Your Vehicle?</h2>
              <p className="text-xl text-white/90 mb-8">
                Schedule your free consultation and quote today
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={() => navigateTo("contact")} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
                  <Calendar size={20} />
                  Schedule Appointment
                </button>
                <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
                  <Phone size={20} />
                  Call: 858-282-8119
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    )
  }

  // Headlight & Taillight Tint Service Page
  const HeadlightTaillightTintPage = () => (
    <div className="pt-20 min-h-screen bg-black">
      <CanonicalTag url="/headlight-taillight-tint" />
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Headlight & Taillight Tint in San Diego</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Professional headlight and taillight tinting services in San Diego providing subtle style enhancement and lens protection. Our precision-cut tint film creates a sleek smoked appearance while maintaining proper light output and DOT compliance.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving North County San Diego including Vista, Carlsbad, and Oceanside, we specialize in headlight tint San Diego installations that balance aesthetics with functionality. From light smoke to darker shades, our taillight tint options let you customize your vehicle's appearance while protecting lenses from UV damage and road debris.
            </p>
            <div className="flex gap-4 justify-center">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => navigateTo("contact")} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Book Tint Appointment
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Tint Options */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tint Options We Offer</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Light Smoke</h3>
              <p className="text-gray-300">Subtle tint providing modest darkening effect while maintaining maximum light output and visibility.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Medium Smoke</h3>
              <p className="text-gray-300">Popular middle-ground option offering noticeable style enhancement with balanced light transmission.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Dark Smoke</h3>
              <p className="text-gray-300">Aggressive smoked appearance for maximum visual impact where regulations allow darker shades.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Clear Protection Film</h3>
              <p className="text-gray-300">Invisible protection option guarding lenses against chips, scratches, and UV damage without darkening.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Benefits of Light Tinting</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">UV Protection</h3>
              <p className="text-gray-300">Film blocks harmful UV rays that cause lens yellowing, hazing, and deterioration over time.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Reduced Fading & Oxidation</h3>
              <p className="text-gray-300">Protective barrier prevents sun damage keeping headlights and taillights clear and vibrant.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Enhanced Appearance</h3>
              <p className="text-gray-300">Sophisticated smoked look adds custom styling that complements your vehicle's overall aesthetic.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Removable & Non-Permanent</h3>
              <p className="text-gray-300">Film removes cleanly when desired, making it perfect for leases or if you change styling preferences.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Mr Wrap for Light Tinting</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Clean Edges</h3>
              <p className="text-gray-300">Precision-cut film with perfect fitment and clean edges. No bubbles, wrinkles, or rough cuts.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Quality Film</h3>
              <p className="text-gray-300">Premium tint materials designed specifically for automotive lighting applications with proper adhesion.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Proper Prep</h3>
              <p className="text-gray-300">Thorough lens cleaning and preparation ensures maximum adhesion and professional, long-lasting results.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">High-End Vehicle Experience</h3>
              <p className="text-gray-300">Extensive experience with luxury and performance vehicles requiring precision light tinting installations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/chrome-delete" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Chrome Delete</h3>
              <p className="text-gray-300">Modern blacked-out aesthetic for trim and badges</p>
            </a>
            <a href="/window-tinting" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Window Tinting</h3>
              <p className="text-gray-300">Premium ceramic tint for heat and UV protection</p>
            </a>
            <a href="/paint-protection-film" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Paint Protection Film</h3>
              <p className="text-gray-300">Self-healing PPF for ultimate paint protection</p>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Headlight & Taillight Tint FAQs</h2>
          <div className="space-y-6">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is headlight and taillight tinting legal?</h3>
              <p className="text-gray-300">Regulations vary by location and light type. We only install tint levels that maintain proper light output where allowed. Light smoke and clear protection film options meet most requirements. We'll recommend compliant shades for your vehicle and location.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does tinting reduce headlight brightness?</h3>
              <p className="text-gray-300">Light smoke has minimal impact on brightness. Medium and dark smoke reduce output more noticeably. We recommend lighter shades for headlights to maintain safe visibility. Taillights can typically accommodate darker tints since brake lights are bright by design.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does light tinting last?</h3>
              <p className="text-gray-300">Quality tint film lasts 3-5 years with proper care. UV exposure and weather conditions affect longevity. Premium films resist fading and peeling better than budget options. We use durable materials designed for exterior automotive applications.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can tint film be removed later?</h3>
              <p className="text-gray-300">Yes. Professional tint film removes cleanly without damaging lenses when removed properly by experts. This makes it perfect for leased vehicles or if you want to change your vehicle's styling in the future without permanent modifications.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does installation take?</h3>
              <p className="text-gray-300">Most headlight and taillight tinting installations take 1-2 hours depending on vehicle complexity and how many lights are being tinted. We ensure proper cleaning, precise cutting, and bubble-free application for professional results.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready for Light Tinting?</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free consultation and light tinting quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigateTo("contact")} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Book Tint Appointment
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
    </div>
  )

  // Caliper Painting Service Page
  const CaliperPaintingPage = () => (
    <div className="pt-20 min-h-screen bg-black">
      <CanonicalTag url="/caliper-painting" />
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Caliper Painting in San Diego</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Professional brake caliper painting in San Diego adding custom color and style to your wheels. Our heat-resistant caliper paint services transform the look behind your wheels while showcasing attention to detail and performance aesthetics.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving North County San Diego including Vista, Carlsbad, and Oceanside, we specialize in caliper painting San Diego for luxury, performance, and custom vehicles. Choose any color to match your vehicle's theme or create eye-catching contrast that highlights your wheels.
            </p>
            <div className="flex gap-4 justify-center">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => navigateTo("contact")} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Book Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Caliper Paint Services */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Caliper Paint Services We Offer</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Custom Color Change</h3>
              <p className="text-gray-300">Any color you can imagine for your brake calipers. Match your vehicle's paint, wheels, or create unique contrast.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">OEM-Style Finishes</h3>
              <p className="text-gray-300">Replicate factory performance colors like Brembo red, BMW blue, Porsche yellow, or AMG silver for authentic look.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Custom Logo Decals</h3>
              <p className="text-gray-300">Add manufacturer logos, custom text, or performance branding to your painted calipers for finishing touch.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Multi-Color Designs</h3>
              <p className="text-gray-300">Two-tone calipers or accent details for truly custom appearance that stands out at car shows and meets.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Our Caliper Painting Process</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <span className="text-cyan-400 text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">Wheel Removal</h3>
              <p className="text-gray-300">Professional wheel removal to access calipers completely. This ensures proper coverage and clean results impossible with wheels on.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <span className="text-cyan-400 text-2xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">Deep Clean & Prep</h3>
              <p className="text-gray-300">Thorough degreasing and surface preparation removes brake dust, oils, and contaminants. Proper prep ensures lasting adhesion.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <span className="text-cyan-400 text-2xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">High-Temp Paint Application</h3>
              <p className="text-gray-300">Heat-resistant caliper paint applied in multiple coats. Designed to withstand extreme brake temperatures without fading or chipping.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <span className="text-cyan-400 text-2xl font-bold">4</span>
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">Cure & Reassemble</h3>
              <p className="text-gray-300">Proper curing time ensures durability. Wheels reinstalled with proper torque specs. Ready to drive with stunning new caliper color.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/chrome-delete" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Chrome Delete</h3>
              <p className="text-gray-300">Complete blackout styling for modern aesthetic</p>
            </a>
            <a href="/headlight-taillight-tint" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Light Tinting</h3>
              <p className="text-gray-300">Smoked headlights and taillights for subtle styling</p>
            </a>
            <a href="/vinyl-wraps" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Vinyl Wraps</h3>
              <p className="text-gray-300">Full color change and custom vehicle wraps</p>
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Mr Wrap for Caliper Painting</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Extreme Durability</h3>
              <p className="text-gray-300">High-temperature paint formulated to withstand brake heat cycles without fading, chipping, or discoloration over time.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Proper Surface Prep</h3>
              <p className="text-gray-300">Meticulous cleaning and degreasing process ensures paint bonds permanently to calipers for professional, lasting results.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Attention to Detail</h3>
              <p className="text-gray-300">Clean paint lines, no overspray, and perfect logo placement. We treat every caliper like it's on a show car.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Performance Vehicle Experience</h3>
              <p className="text-gray-300">Extensive experience with high-performance and luxury vehicles. We understand what enthusiasts expect from caliper work.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Caliper Painting FAQs</h2>
          <div className="space-y-6">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does caliper paint last?</h3>
              <p className="text-gray-300">Quality high-temperature caliper paint lasts 3-5+ years with proper preparation and application. The paint is designed to withstand extreme brake heat without fading or peeling. Longevity depends on driving conditions and brake usage intensity.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can caliper paint handle brake heat?</h3>
              <p className="text-gray-300">Absolutely. We use specialized high-temperature paint rated to withstand 500°F+ temperatures. This paint is specifically formulated for brake calipers and maintains color and adhesion through repeated heat cycles from normal and performance driving.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What colors are available for calipers?</h3>
              <p className="text-gray-300">Any color you want! Popular choices include red, yellow, blue, silver, black, orange, and green. We can match your vehicle's paint, replicate OEM performance colors (Brembo red, Porsche yellow, BMW blue), or create custom colors. Bring a sample or color code.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does caliper painting take?</h3>
              <p className="text-gray-300">Most caliper painting jobs take 1-2 days depending on how many calipers and whether custom logos or multi-color work is involved. This includes wheel removal, thorough prep, multiple paint coats, proper curing time, and reinstallation. We never rush caliper work.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does painting calipers affect braking performance?</h3>
              <p className="text-gray-300">No. Professional caliper painting has zero impact on braking performance. Paint is applied only to external caliper surfaces, never to internal components, pistons, or brake pads. Your brakes function exactly as before, just with custom color.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Transform Your Calipers?</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free caliper painting consultation and quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigateTo("contact")} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Book Consultation
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
    </div>
  )

  // Chrome Delete Service Page
  const ChromeDeletePage = () => (
    <div className="pt-20 min-h-screen bg-black">
      <CanonicalTag url="/chrome-delete" />
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Chrome Delete & Blackout Services in San Diego</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Transform your vehicle's appearance with professional chrome delete and blackout trim services in San Diego. Achieve a sleek, modern aesthetic by converting chrome trim, badges, and accents to gloss, satin, or matte black finishes.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving North County San Diego including Vista, Carlsbad, and Oceanside, we specialize in precision chrome delete installations that create a cohesive blackout trim look on luxury and performance vehicles. Perfect for Tesla, BMW, Mercedes, Porsche, and all makes seeking modern styling.
            </p>
            <div className="flex gap-4 justify-center">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => navigateTo("contact")} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Book Chrome Delete
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Chrome Delete Services */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Chrome Delete Services We Offer</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Window Trim Blackout</h3>
              <p className="text-gray-300">Complete window trim and pillar blackout for seamless modern appearance around all glass surfaces.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Grille Blackout</h3>
              <p className="text-gray-300">Front grille chrome conversion creating aggressive, blacked-out front fascia styling.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Badge & Emblem Blackout</h3>
              <p className="text-gray-300">Logo and emblem blackout for subtle branding and cohesive color scheme throughout the vehicle.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Roof Rails & Accents</h3>
              <p className="text-gray-300">Roof rail blackout and accent trim conversion for SUVs and crossovers seeking complete transformation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Finish Options */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Finish Options</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Gloss Black</h3>
              <p className="text-gray-300">High-shine gloss black finish for bold, reflective appearance matching gloss black wheels and accents.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Satin Black</h3>
              <p className="text-gray-300">Subtle sheen satin black finish offering refined, sophisticated look between gloss and matte.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Matte Black</h3>
              <p className="text-gray-300">Non-reflective matte black finish for aggressive, stealthy appearance popular on performance vehicles.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/window-tinting" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Window Tinting</h3>
              <p className="text-gray-300">Complete the blackout look with ceramic window tint</p>
            </a>
            <a href="/headlight-taillight-tint" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Light Tinting</h3>
              <p className="text-gray-300">Smoked lights for aggressive styling</p>
            </a>
            <a href="/paint-protection-film" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Paint Protection Film</h3>
              <p className="text-gray-300">Ultimate protection for your vehicle's paint</p>
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Mr Wrap for Chrome Delete San Diego</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Clean Edges</h3>
              <p className="text-gray-300">Precision-cut vinyl with clean edges and seamless application. No visible overlaps or rough cuts.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Proper Prep</h3>
              <p className="text-gray-300">Thorough surface preparation and degreasing ensures maximum adhesion and long-lasting results.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Durable Materials</h3>
              <p className="text-gray-300">Premium 3M and Avery wrap materials designed for exterior use with UV resistance and longevity.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Luxury Experience</h3>
              <p className="text-gray-300">Extensive experience with luxury brands including Tesla, BMW, Mercedes, Porsche, and exotic vehicles.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Chrome Delete FAQs</h2>
          <div className="space-y-6">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does chrome delete last?</h3>
              <p className="text-gray-300">Premium vinyl chrome delete typically lasts 5-7 years with proper care and maintenance. Indoor storage and regular cleaning extend lifespan. Quality materials and professional installation ensure maximum durability against sun exposure and weather conditions.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is chrome delete removable?</h3>
              <p className="text-gray-300">Yes, chrome delete vinyl is fully removable. When removed properly by professionals, the vinyl comes off cleanly without damaging the original chrome underneath. This makes chrome delete perfect for leased vehicles or if you want to change styling in the future.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does chrome delete damage chrome?</h3>
              <p className="text-gray-300">No. Professional chrome delete installation actually protects your original chrome from UV fading, oxidation, and environmental damage. The vinyl acts as a protective barrier. When removed by experts, your factory chrome is preserved underneath in original condition.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does chrome delete installation take?</h3>
              <p className="text-gray-300">Most chrome delete installations take 4-8 hours depending on vehicle complexity and how much trim is being blacked out. Full vehicle chrome delete with windows, grille, badges, and accents takes a full day. Partial trim blackout can be completed in half a day.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can chrome delete be done with a full wrap or PPF?</h3>
              <p className="text-gray-300">Absolutely! Chrome delete is commonly paired with <a href="/car-wrap-san-diego" className="text-cyan-400 hover:underline">full color change wraps</a> or paint protection film installations. We can coordinate the timing to complete chrome delete along with your wrap or PPF project for a complete transformation in one visit.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready for Chrome Delete San Diego?</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free chrome delete consultation and quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigateTo("contact")} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Book Chrome Delete
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
    </div>
  )

  // Window Tinting Service Page
  const WindowTintingPage = () => (
    <div className="pt-20 min-h-screen bg-black">
      <CanonicalTag url="/window-tinting" />
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Window Tinting in San Diego</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Professional automotive window tinting in San Diego providing superior heat rejection, UV protection, and enhanced privacy. Our premium ceramic window tint installations deliver comfort, style, and protection for your vehicle.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving North County San Diego including Vista, Carlsbad, and Oceanside, we specialize in ceramic and carbon tint installations that meet California legal requirements while maximizing heat reduction and interior protection.
            </p>
            <div className="flex gap-4 justify-center">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => navigateTo("contact")} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Book Tint Appointment
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Tint Options */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Window Tint Options</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Ceramic Tint</h3>
              <p className="text-gray-300">Premium ceramic technology offering maximum heat rejection, superior UV protection, and crystal-clear visibility without signal interference.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Carbon Tint</h3>
              <p className="text-gray-300">Advanced carbon film providing excellent heat reduction, UV blocking, and non-metallic construction for no signal interference.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Vehicle Tint</h3>
              <p className="text-gray-300">Comprehensive window tinting for all side and rear windows, creating uniform appearance and maximum interior protection.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Windshield Tint</h3>
              <p className="text-gray-300">Light tint strip options for windshields where allowed, reducing glare and heat while maintaining safe visibility and legal compliance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Benefits of Window Tinting</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Heat Reduction</h3>
              <p className="text-gray-300">Ceramic tint blocks up to 99% of infrared heat, keeping your vehicle significantly cooler in San Diego's hot climate.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">UV Protection</h3>
              <p className="text-gray-300">Block 99% of harmful UV rays that cause skin damage, interior fading, and dashboard cracking.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Interior Protection</h3>
              <p className="text-gray-300">Preserve your vehicle's interior by preventing sun damage to seats, dashboard, and trim materials.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Improved Comfort</h3>
              <p className="text-gray-300">Reduce glare, maintain cooler cabin temperature, and enhance privacy for a more comfortable driving experience.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/paint-protection-film" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Paint Protection Film</h3>
              <p className="text-gray-300">Self-healing film for ultimate paint protection</p>
            </a>
            <a href="/chrome-delete" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Chrome Delete</h3>
              <p className="text-gray-300">Blacked-out trim for modern appearance</p>
            </a>
            <a href="/vinyl-wraps" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Vinyl Wraps</h3>
              <p className="text-gray-300">Complete color change and custom designs</p>
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Mr Wrap for Window Tinting</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Clean Installations</h3>
              <p className="text-gray-300">Precision-cut film and expert installation techniques ensure bubble-free, perfectly fitted tint on every window.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Quality Film</h3>
              <p className="text-gray-300">We use only premium ceramic and carbon films from trusted manufacturers, never cheap dyed films that fade.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Check className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Lifetime Warranty</h3>
              <p className="text-gray-300">Our ceramic tint comes with lifetime warranty coverage against fading, bubbling, peeling, and purple discoloration.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Window Tinting FAQs</h2>
          <div className="space-y-6">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is window tinting legal in California?</h3>
              <p className="text-gray-300">Yes, with restrictions. California allows tint on rear and rear side windows at any darkness. Front side windows must allow more than 70% light transmission. We ensure all installations meet California legal requirements.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How dark should I tint my windows?</h3>
              <p className="text-gray-300">For rear windows, we recommend 20% (dark) or 35% (medium-dark) tint. Front side windows in California must be 70% or lighter for legal compliance. We'll help you choose the perfect shade for your needs and preferences.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does window tinting take?</h3>
              <p className="text-gray-300">Most vehicles take 2-4 hours for full tint installation. We never rush installations—proper prep work and careful application ensure lasting quality and bubble-free results.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does tint take to cure?</h3>
              <p className="text-gray-300">Tint needs 3-5 days to fully cure and dry. During this time, you may see small water bubbles or haze—this is normal and will disappear. Avoid rolling windows down for 3-5 days after installation.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How do I care for tinted windows?</h3>
              <p className="text-gray-300">Wait 5 days before cleaning. Use ammonia-free cleaner and soft microfiber cloth. Avoid abrasive materials or paper towels that can scratch the film. Proper care ensures your tint lasts for years.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What are the benefits of window tint?</h3>
              <p className="text-gray-300">Window tint reduces heat, blocks UV rays, improves interior comfort, and helps protect upholstery from fading.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What is the difference between standard and ceramic tint?</h3>
              <p className="text-gray-300">Ceramic tint offers improved heat rejection and clarity compared to standard films, without interfering with electronics or visibility.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is window tint legal in California?</h3>
              <p className="text-gray-300">Yes, when installed within California's tint regulations. We help clients choose compliant options.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does window tint last?</h3>
              <p className="text-gray-300">Quality window tint can last many years when professionally installed and properly cared for.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Informational Text */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-6 text-white">Window Tint in San Diego</h2>
          <div className="space-y-4 text-gray-300 leading-relaxed text-lg">
            <p>
              Professional window tint improves comfort, privacy, and protection while enhancing the overall look of your vehicle. At Auto Wraps San Diego, we install high-quality automotive window tint designed to reduce heat, block harmful UV rays, and minimize interior fading caused by sun exposure.
            </p>
            <p>
              Our window tint options range from standard films to advanced ceramic tint, offering superior heat rejection without compromising visibility or signal performance. Proper installation ensures a clean, bubble-free finish that complies with California tint regulations while delivering long-term durability.
            </p>
            <p>
              Window tint is one of the most effective upgrades for daily drivers, luxury vehicles, and performance cars, helping maintain a cooler interior and reduce strain on your vehicle's climate system. Each installation is carefully tailored to the vehicle and driver preferences for shade, performance, and appearance.
            </p>
            <p>
              Serving Vista, Oceanside, Carlsbad, Encinitas, and the greater San Diego area, we provide professional window tint services for cars, trucks, SUVs, and fleet vehicles looking for reliable heat and UV protection.
            </p>
          </div>
        </div>
      </section>

      {/* Service Area Link */}
      <section className="py-12 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-gray-300">
            Proudly serving <a href="https://autowrapsandiego.com/oceanside-ppf-wrap" className="text-cyan-400 hover:text-cyan-300 underline transition">Oceanside</a> and surrounding North County San Diego areas.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Tint Your Windows?</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free window tinting consultation and quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigateTo("contact")} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Book Tint Appointment
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
    </div>
  )

  // Commercial Fleet Wraps Service Page
  const CommercialFleetWrapsPage = () => (
    <div className="pt-20 min-h-screen bg-black">
      <CanonicalTag url="/commercial-fleet-wraps" />
      <Helmet>
        <title>Commercial Fleet Wraps San Diego | Vehicle Wraps for Businesses | Mr Wrap</title>
        <meta name="description" content="Commercial fleet wraps and vehicle graphics for San Diego businesses — vans, trucks, and service vehicles. Professional installation at Mr Wrap in Vista. Get a free quote today." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How long do commercial vehicle wraps last?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Commercial-grade vinyl wraps typically last 5-7 years with proper care. Fleet vehicles in daily use can expect 3-5 years depending on mileage, road conditions, and maintenance. Premium materials and professional installation maximize longevity."
            }
          },
          {
            "@type": "Question",
            "name": "Can you handle multiple vehicles at once?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes! We specialize in fleet installations and can wrap multiple vehicles simultaneously or on a rolling schedule to minimize business disruption. We coordinate timing to keep your fleet operational while completing installations efficiently."
            }
          },
          {
            "@type": "Question",
            "name": "Do you provide design services?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Absolutely. Our in-house designers create professional fleet graphics from scratch or work with your existing branding. We handle logo recreation, layout design, and ensure graphics are optimized for vehicle application and road visibility."
            }
          },
          {
            "@type": "Question",
            "name": "Should I choose partial or full wrap for my fleet?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Depends on budget and branding goals. Full wraps maximize brand exposure and vehicle protection. Partial wraps (door graphics, tailgate designs) offer professional appearance at lower cost. We'll recommend the best solution based on your business needs."
            }
          },
          {
            "@type": "Question",
            "name": "What's the typical turnaround time for fleet wraps?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Single vehicles take 2-3 days including prep and installation. Multi-vehicle fleets depend on quantity and complexity, and we provide detailed timelines during consultation. Rush services available for time-sensitive projects with advance scheduling."
            }
          },
          {
            "@type": "Question",
            "name": "What types of vehicles can you wrap for a fleet?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We wrap all types of commercial vehicles including cargo vans, box trucks, pickup trucks, service vehicles, delivery vans, and trailers. Each wrap is designed for the specific vehicle to keep your branding clear and consistent across your fleet."
            }
          },
          {
            "@type": "Question",
            "name": "Can a fleet wrap be removed later?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. When removed by a professional, vinyl wrap comes off cleanly and helps preserve the paint underneath, since it has been shielded from everyday sun exposure and surface wear while installed. This makes wraps a good option if you plan to resell or repurpose a vehicle down the road."
            }
          }
        ]
      }) }} />
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">Commercial Fleet & Vehicle Wraps in San Diego</h1>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Turn your work vehicle into advertising for your business with professional commercial vehicle wraps, fleet graphics and partial wraps from Mr Wrap.
            </p>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Located in Vista, CA, we provide commercial wraps for businesses throughout North County and San Diego, from a single work truck or service van to growing company fleets. For personal vehicles, we also install <a href="/car-wrap-san-diego" className="text-cyan-400 hover:underline">car wraps in San Diego</a>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#fleet-quote-form" className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-4 rounded-lg font-bold transition min-h-[48px]">
                <Calendar size={18} />
                GET A COMMERCIAL WRAP QUOTE
              </a>
              <a href="tel:858-282-8119" className="flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-4 rounded-lg font-bold transition min-h-[48px]">
                <Phone size={18} />
                CALL 858-282-8119
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Real Commercial Projects */}
      <section className="py-16 sm:py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-white text-center">Real Commercial Vehicle Wraps by Mr Wrap</h2>

          <div className="space-y-16">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-white">Commercial Van Wrap — Senior Helpers</h3>
              <p className="text-gray-300 mb-6 max-w-3xl">
                See the transformation from a plain white cargo van into a professionally branded commercial vehicle. Mr Wrap installs custom printed commercial vehicle wraps, partial wraps and fleet graphics for businesses throughout Vista, North County and San Diego.
              </p>
              <BeforeAfterSlider
                beforeImage="https://static.lumi.new/14/141ba768f707d598b2fa26632afd8a4f.webp"
                afterImage="https://static.lumi.new/6f/6f1b51109d37d00f3e4c3071fe99114b.webp"
                beforeAlt="Plain white cargo van before commercial wrap installation"
                afterAlt="Senior Helpers cargo van after custom printed commercial wrap with hiring graphics"
                beforeLabel="Before"
                afterLabel="After"
                projectTitle="Senior Helpers commercial van wrap"
                serviceBadge="Commercial Van Wrap"
              />
            </div>

            <div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-white">Commercial Truck Graphics — North County San Diego</h3>
              <p className="text-gray-300 mb-6 max-w-3xl">
                Commercial truck graphics installed for a North County business, creating clear, professional branding while keeping much of the vehicle’s original finish visible.
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="overflow-hidden rounded-2xl border border-cyan-500/20 bg-black aspect-[4/5]">
                  <img
                    src="https://static.lumi.new/90/901c45d72a33a47b0ea9f7111863bc8c.webp"
                    alt="Unbranded pickup truck in the Mr Wrap shop before commercial graphics"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={1200}
                    height={1600}
                  />
                </div>
                <div className="overflow-hidden rounded-2xl border border-cyan-500/20 bg-black aspect-[4/5]">
                  <img
                    src="https://static.lumi.new/0e/0e4d49381c11f54c92342182af81a172.webp"
                    alt="White Toyota pickup in the shop with Small Offices door graphics being installed"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={1200}
                    height={1600}
                  />
                </div>
                <div className="overflow-hidden rounded-2xl border border-cyan-500/20 bg-black aspect-[4/5]">
                  <img
                    src="https://static.lumi.new/75/758fd1cff246b51053ed50137c1738ca.webp"
                    alt="Finished Small Offices 4 Rent pickup truck with door graphics and phone number"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={1600}
                    height={1200}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <a href="#fleet-quote-form" className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 rounded-lg transition min-h-[48px]">
              <Calendar size={20} />
              Need to Brand Your Work Vehicle? Get a Commercial Wrap Quote
            </a>
          </div>
        </div>
      </section>

      {/* Local relevance */}
      <section className="py-16 sm:py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white text-center">Commercial Vehicle Wraps in Vista & North County San Diego</h2>
          <p className="text-lg text-gray-300 leading-relaxed mb-4">
            Mr Wrap is located in Vista and provides commercial vehicle wraps, fleet graphics, partial wraps and business vehicle graphics for companies throughout North County San Diego. We work with businesses in Vista, Oceanside, Carlsbad, San Marcos, Escondido and surrounding San Diego communities.
          </p>
          <p className="text-lg text-gray-300 leading-relaxed mb-4">
            Whether you're branding your first company vehicle or expanding an existing fleet, we can create consistent graphics that help customers recognize your business everywhere your vehicles go.
          </p>
          <p className="text-lg text-gray-300 leading-relaxed">
            Most North County businesses can drop off vehicles at our Vista shop, and we offer flexible scheduling, including after-hours options, to keep your vehicles on the road while we work on your fleet.
          </p>
        </div>
      </section>

      {/* Commercial Wrap Intake Form */}
      <section id="fleet-quote-form" className="py-20 bg-gray-900 scroll-mt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">Request Fleet Wrap Quote</h2>
            <p className="text-xl text-gray-300">Tell us about your commercial vehicle project and we'll provide a detailed quote</p>
          </div>

          <div className="bg-black rounded-2xl shadow-xl p-8 border border-cyan-500/20">
            <FleetQuoteForm />
          </div>
        </div>
      </section>

      {/* Commercial Wrap Services */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Commercial Wrap Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Fleet Wraps</h3>
              <p className="text-gray-300">Complete vehicle transformation with comprehensive coverage showcasing your brand, logo, contact information, and services across the entire vehicle exterior.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Wraps & Logo Installs</h3>
              <p className="text-gray-300">Strategic graphics placement on doors, tailgates, or vehicle sides for cost-effective branding that maintains professional appearance while fitting any budget.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Commercial Van Wraps</h3>
              <p className="text-gray-300">Custom printed graphics for cargo vans and service vans so your company name, phone number, and services are easy to see on the road.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Commercial Truck Wraps & Graphics</h3>
              <p className="text-gray-300">Door logos, bed and tailgate graphics, or larger truck wraps that keep branding clear without overcomplicating the design.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Contractor & Service Vehicle Graphics</h3>
              <p className="text-gray-300">Specialized wraps for plumbers, electricians, HVAC, landscaping, and service professionals. Durable materials that withstand job site conditions while advertising your business.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Box Truck & Trailer Wraps</h3>
              <p className="text-gray-300">Large-format wraps for delivery trucks, box trucks, and trailers. Maximum visibility with bold graphics designed for highway viewing and brand recognition.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Design Support</h3>
              <p className="text-gray-300">In-house graphic design services to create professional, eye-catching fleet graphics. We handle everything from concept to print-ready files and installation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Fleet Wraps Work for Your Business */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Fleet Wraps Work for Your Business</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-3 text-white">Moving Advertising</h3>
              <p className="text-gray-300">Every time your wrapped vehicle is on the road or parked at a job site, it's advertising your business to people who see it, with no ongoing media spend after installation.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-3 text-white">Professional First Impression</h3>
              <p className="text-gray-300">A clean, consistently branded vehicle signals that your business is established and takes pride in its work, helping build trust before a customer even calls.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-3 text-white">Consistent Brand Identity</h3>
              <p className="text-gray-300">Wrapping every vehicle in your fleet with the same colors, logo, and contact information keeps your brand recognizable across every job site and neighborhood you work in.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-3 text-white">Helps Protect the Paint Underneath</h3>
              <p className="text-gray-300">Vinyl wrap also acts as a layer over your factory paint, helping shield it from everyday sun exposure and minor surface wear while your fleet is in use.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-3 text-white">Works for Any Fleet Size</h3>
              <p className="text-gray-300">Whether you're branding a single work truck or an entire fleet, wraps scale to your business. Start with one vehicle and add more as you grow.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-3 text-white">Flexible & Reversible</h3>
              <p className="text-gray-300">Vinyl wraps can be removed when a vehicle is sold, leased, or repurposed, giving you the flexibility to update your branding without permanent changes to the vehicle.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Industries We Serve</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <p className="text-white font-semibold">Contractors & Construction</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <p className="text-white font-semibold">Plumbing & HVAC</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <p className="text-white font-semibold">Electrical Services</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <p className="text-white font-semibold">Landscaping & Lawn Care</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <p className="text-white font-semibold">Delivery & Logistics</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <p className="text-white font-semibold">Real Estate</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <p className="text-white font-semibold">Food Service & Catering</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <p className="text-white font-semibold">Cleaning & Janitorial Services</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <p className="text-white font-semibold">All Commercial Fleets</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/vinyl-wraps" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Vinyl Wraps</h3>
              <p className="text-gray-300">Personal vehicle color change and custom wraps</p>
            </a>
            <a href="/paint-protection-film" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Paint Protection Film</h3>
              <p className="text-gray-300">Premium PPF for fleet vehicle protection</p>
            </a>
            <a href="/window-tinting" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Window Tinting</h3>
              <p className="text-gray-300">Commercial tint for fleet comfort and branding</p>
            </a>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Our Fleet Wrap Process</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <span className="text-cyan-400 text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">Consultation</h3>
              <p className="text-gray-300">We start with a quick consultation to understand your fleet, goals, and budget, then recommend the right coverage, partial or full wrap, for your vehicles.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <span className="text-cyan-400 text-2xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">Design</h3>
              <p className="text-gray-300">Collaborate on custom graphics that showcase your brand and message. We create designs optimized for vehicle dimensions and viewing distance, and review mockups with you before production.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <span className="text-cyan-400 text-2xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">Installation</h3>
              <p className="text-gray-300">Expert installation using premium materials and proven techniques. We schedule efficiently to minimize downtime for your business vehicles.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <span className="text-cyan-400 text-2xl font-bold">4</span>
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">Ongoing Support</h3>
              <p className="text-gray-300">After installation we're available for care questions, minor repairs, or expanding your wrap program as your fleet grows.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Businesses Choose Mr Wrap</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Premium Quality</h3>
              <p className="text-gray-300">Commercial-grade materials from 3M and Avery designed for fleet durability and long-term performance in business use.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Brand Consistency</h3>
              <p className="text-gray-300">Precise color matching and standardized designs ensure your entire fleet maintains cohesive, professional branding across all vehicles.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Users className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Scalable Solutions</h3>
              <p className="text-gray-300">Whether one vehicle or an entire fleet, we handle projects of any size with volume discounts for multi-vehicle installations.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Clock className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Flexible Scheduling</h3>
              <p className="text-gray-300">We work around your business schedule to minimize vehicle downtime. Weekend and after-hours appointments available for fleets.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Commercial Fleet Wrap FAQs</h2>
          <div className="space-y-6">
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long do commercial vehicle wraps last?</h3>
              <p className="text-gray-300">Commercial-grade vinyl wraps typically last 5-7 years with proper care. Fleet vehicles in daily use can expect 3-5 years depending on mileage, road conditions, and maintenance. Premium materials and professional installation maximize longevity.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can you handle multiple vehicles at once?</h3>
              <p className="text-gray-300">Yes! We specialize in fleet installations and can wrap multiple vehicles simultaneously or on a rolling schedule to minimize business disruption. We coordinate timing to keep your fleet operational while completing installations efficiently.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Do you provide design services?</h3>
              <p className="text-gray-300">Absolutely. Our in-house designers create professional fleet graphics from scratch or work with your existing branding. We handle logo recreation, layout design, and ensure graphics are optimized for vehicle application and road visibility.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Should I choose partial or full wrap for my fleet?</h3>
              <p className="text-gray-300">Depends on budget and branding goals. Full wraps maximize brand exposure and vehicle protection. Partial wraps (door graphics, tailgate designs) offer professional appearance at lower cost. We'll recommend the best solution based on your business needs and ROI goals.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What's the typical turnaround time for fleet wraps?</h3>
              <p className="text-gray-300">Single vehicles take 2-3 days including prep and installation. Multi-vehicle fleets depend on quantity and complexity, and we provide detailed timelines during consultation. Rush services available for time-sensitive projects with advance scheduling.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What types of vehicles can you wrap for a fleet?</h3>
              <p className="text-gray-300">We wrap all types of commercial vehicles including cargo vans, box trucks, pickup trucks, service vehicles, delivery vans, and trailers. Each wrap is designed for the specific vehicle to keep your branding clear and consistent across your fleet.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can a fleet wrap be removed later?</h3>
              <p className="text-gray-300">Yes. When removed by a professional, vinyl wrap comes off cleanly and helps preserve the paint underneath, since it has been shielded from everyday sun exposure and surface wear while installed. This makes wraps a good option if you plan to resell or repurpose a vehicle down the road.</p>
            </div>
          </div>
        </div>
      </section>



      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Brand Your Fleet?</h2>
          <p className="text-xl text-white/90 mb-8">
            Request your free fleet wrap consultation and volume pricing quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#fleet-quote-form" className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2 min-h-[48px]">
              <Calendar size={20} />
              Request Fleet Quote
            </a>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2 min-h-[48px]">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
    </div>
  )

  // Vinyl Wraps Service Page
  const VinylWrapsPage = () => (
    <div className="pt-20 min-h-screen bg-black">
      <CanonicalTag url="/vinyl-wraps" />
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Vinyl Wraps in San Diego</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Transform your vehicle's appearance with <a href="/car-wrap-san-diego" className="text-cyan-400 hover:underline">professional vinyl wraps in San Diego</a>. From full color changes to custom designs, our expert installers deliver stunning results using premium Avery Dennison, 3M, and specialty wrap films.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving North County San Diego including Vista, Carlsbad, and Oceanside, we specialize in color change wraps, partial wraps, chrome delete, and <a href="/commercial-fleet-wraps" className="text-cyan-400 hover:underline">commercial fleet graphics</a>. Whether you want a complete transformation or subtle accents, vinyl wrap North County experts deliver flawless installations.
            </p>
            <div className="flex gap-4 justify-center">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => navigateTo("contact")} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Book Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Vinyl Wrap Services */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Vinyl Wrap Services We Offer</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Color Change Wraps</h3>
              <p className="text-gray-300">Complete vehicle transformation with premium films in thousands of colors and finishes. Change your car's entire look while protecting the original paint.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Wraps</h3>
              <p className="text-gray-300">Accent wraps for roofs, hoods, mirrors, and trim pieces to create unique two-tone designs and custom styling.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Satin, Matte & Gloss Finishes</h3>
              <p className="text-gray-300">Choose from satin, matte, gloss, metallic, chrome, and specialty finishes to match your vision perfectly.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Chrome Delete & Blackout</h3>
              <p className="text-gray-300">Modern blacked-out aesthetic by wrapping chrome trim, badges, and accents in gloss or matte black vinyl.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Custom Printed Wraps</h3>
              <p className="text-gray-300">Unique printed graphics, patterns, and designs created specifically for your vehicle with professional installation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Related Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a href="/paint-protection-film" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Paint Protection Film</h3>
              <p className="text-gray-300">Add PPF over wraps for dual protection</p>
            </a>
            <a href="/colored-ppf-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Colored PPF San Diego</h3>
              <p className="text-gray-300">Color change PPF that protects while it transforms</p>
            </a>
            <a href="/chrome-delete" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Chrome Delete</h3>
              <p className="text-gray-300">Blackout trim to complement your wrap</p>
            </a>
            <a href="/window-tinting" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Window Tinting</h3>
              <p className="text-gray-300">Complete transformation with ceramic tint</p>
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Mr Wrap for Vinyl Wraps</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">13 Years of Experience</h3>
              <p className="text-gray-300">13 years of professional vinyl wrap installation in San Diego and North County.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Experienced Installers</h3>
              <p className="text-gray-300">Experienced technicians with hands-on expertise across major wrap brands and materials.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Premium Wrap Films</h3>
              <p className="text-gray-300">We install Avery Dennison, 3M, and other professional-grade wrap materials selected for lasting quality.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Proper Disassembly</h3>
              <p className="text-gray-300">Professional component removal when required for seamless, edge-to-edge coverage.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicles We Wrap */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-8 text-white text-center">Vehicles We Wrap</h2>
          <p className="text-xl text-gray-300 text-center mb-12">
            Expert vinyl wrap installation for all makes and models
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Tesla</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">BMW</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Porsche</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Mercedes</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Trucks & SUVs</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Exotic Cars</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Commercial Fleet</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">All Makes</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Vinyl Wrap FAQs</h2>
          <div className="space-y-6">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does a vinyl wrap last?</h3>
              <p className="text-gray-300">A professionally installed vinyl wrap typically lasts several years depending on material quality, exposure, and maintenance.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does vinyl wrap protect paint?</h3>
              <p className="text-gray-300">Yes. Vinyl wrap helps protect original paint from UV exposure, light scratches, and surface wear while it is installed.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can a vinyl wrap be removed?</h3>
              <p className="text-gray-300">Yes. Vinyl wraps are removable and allow the vehicle to return to its original paint when properly removed.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is vinyl wrap better than paint?</h3>
              <p className="text-gray-300">Vinyl wrap offers flexibility, customization, and reversibility without permanent changes, making it a popular alternative to repainting.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Informational Text */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-6 text-white">Vinyl Wrap & Color Change in San Diego</h2>
          <div className="space-y-4 text-gray-300 leading-relaxed text-lg">
            <p>
              Vinyl wraps are a versatile way to transform the appearance of your vehicle while protecting the original paint underneath. At Auto Wraps San Diego, we specialize in professional vinyl wrap installations for full color changes, partial wraps, accents, and custom designs using high-quality cast vinyl films.
            </p>
            <p>
              A properly installed vinyl wrap allows you to change colors, finishes, or styling without permanent paintwork. Wraps can help protect factory paint from sun fade, light scratches, and environmental exposure while offering the flexibility to return the vehicle to stock when removed.
            </p>
            <p>
              We install vinyl wraps on a wide range of vehicles, including Teslas, luxury cars, performance vehicles, trucks, and SUVs. Each project is carefully planned to ensure clean edges, consistent finish, and long-term durability. From gloss and satin finishes to matte and specialty textures, our wrap installations are tailored to match each client's goals.
            </p>
            <p>
              Serving Vista, Oceanside, Carlsbad, Encinitas, and the greater San Diego area, we provide vinyl wrap solutions for both personal vehicles and commercial branding projects, delivering clean, professional results you can be confident in.
            </p>
          </div>
        </div>
      </section>

      {/* Service Area Link */}
      <section className="py-12 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-gray-300">
            Serving <a href="https://autowrapsandiego.com/north-county-vinyl-wraps" className="text-cyan-400 hover:text-cyan-300 underline transition">North County San Diego</a>, including Oceanside, Vista, Carlsbad, and surrounding areas.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Transform Your Vehicle?</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free vinyl wrap consultation and quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigateTo("contact")} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Book Consultation
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
    </div>
  )

  // Paint Protection Film Service Page
  const PaintProtectionFilmPage = () => (
    <div className="pt-20 min-h-screen bg-black">
      <CanonicalTag url="/paint-protection-film" />
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Paint Protection Film in San Diego</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Protect your vehicle's paint with premium paint protection film. Self-healing technology, manufacturer warranty coverage that varies by film, and a crystal-clear finish that helps preserve your car's appearance while defending against rock chips, scratches, and environmental damage.
            </p>
            <div className="flex gap-4 justify-center">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => navigateTo("contact")} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Book Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Options */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">PPF Coverage Options</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Front Kit</h3>
              <p className="text-gray-300 mb-4">Comprehensive protection for high-impact areas including hood, fenders, bumper, mirrors, and headlights.</p>
              <ul className="space-y-2 text-gray-400">
                <li>• Complete hood coverage</li>
                <li>• Full front bumper</li>
                <li>• Both fenders</li>
                <li>• Side mirrors</li>
                <li>• Headlights</li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Vehicle</h3>
              <p className="text-gray-300 mb-4">Ultimate protection covering every painted surface on your vehicle for maximum peace of mind.</p>
              <ul className="space-y-2 text-gray-400">
                <li>• Complete exterior coverage</li>
                <li>• All panels protected</li>
                <li>• Maintains resale value</li>
                <li>• Showroom condition preserved</li>
                <li>• Best long-term investment</li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Coverage</h3>
              <p className="text-gray-300 mb-4">Strategic protection for specific vulnerable areas based on your driving needs and budget.</p>
              <ul className="space-y-2 text-gray-400">
                <li>• Rocker panels</li>
                <li>• Door edges</li>
                <li>• A-pillars</li>
                <li>• Rear bumper</li>
                <li>• Custom configurations</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Premium Paint Protection Film Benefits</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Self-Healing Technology</h3>
              <p className="text-gray-300">Minor scratches and swirl marks disappear with heat from the sun or warm water.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Hydrophobic Coating</h3>
              <p className="text-gray-300">Water and dirt bead off easily, making your car easier to clean and maintain.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">UV Protection</h3>
              <p className="text-gray-300">Blocks harmful UV rays that cause paint fading and oxidation over time.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Check className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Manufacturer Warranty</h3>
              <p className="text-gray-300">Premium films typically include manufacturer coverage against yellowing, cracking, peeling, and hazing. Exact terms depend on the product selected.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicles We Protect */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-8 text-white text-center">Vehicles We Protect</h2>
          <p className="text-xl text-gray-300 text-center mb-12">
            Expert PPF installation for all makes and models
          </p>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto">
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Tesla</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">BMW</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Porsche</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Mercedes</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Audi</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Range Rover</p>
            </div>
            <a href="/corvette-ppf-san-diego" className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20 hover:border-cyan-500 transition">
              <p className="text-white font-semibold">Corvette</p>
            </a>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Maserati</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Rolls Royce</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Cybertruck</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Exotic Cars</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">All Makes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/vinyl-wraps" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Vinyl Wraps</h3>
              <p className="text-gray-300">Color change wraps over PPF for dual protection</p>
            </a>
            <a href="/window-tinting" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Window Tinting</h3>
              <p className="text-gray-300">Ceramic tint for comprehensive protection</p>
            </a>
            <a href="/chrome-delete" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Chrome Delete</h3>
              <p className="text-gray-300">Blackout styling to enhance PPF aesthetics</p>
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Mr Wrap */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Mr Wrap for PPF</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Professional Installation</h3>
              <p className="text-gray-300">Experienced installers focused on clean prep, careful fitment, and workmanship that supports applicable film warranties.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">13 Years of Experience</h3>
              <p className="text-gray-300">13 years of professional installation experience protecting San Diego vehicles with careful prep and fitment.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Zap className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Precision Installation</h3>
              <p className="text-gray-300">We select bulk film or precision-pattern methods based on the vehicle and panel, with wrapped or tucked edges where appropriate.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Full Disassembly</h3>
              <p className="text-gray-300">Professional bumper removal for seamless edge-to-edge coverage on every installation.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Indoor Installation Bay</h3>
              <p className="text-gray-300">Installations are completed in our indoor Vista shop bay so we can control lighting, cleanliness, and working conditions.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Users className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Tesla Specialists</h3>
              <p className="text-gray-300">Expert installers specializing in Tesla Model 3, Y, S, and X with precision patterns.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Informational Text */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-6 text-white">Paint Protection Film (PPF) in San Diego</h2>
          <div className="space-y-4 text-gray-300 leading-relaxed text-lg">
            <p>
              Paint Protection Film (PPF) is the most effective way to protect your vehicle's paint from rock chips, road debris, scratches, bug etching, and UV damage. At Auto Wraps San Diego, we install premium self-healing PPF designed to preserve your vehicle's finish while maintaining a clean, factory appearance.
            </p>
            <p>
              Our PPF installations are customized to each vehicle based on driving habits, paint condition, and long-term ownership goals. Whether you're protecting a daily driver, luxury vehicle, or performance car, PPF provides a durable barrier that helps maintain resale value and reduce the need for paint correction or repainting in the future.
            </p>
            <p>
              We specialize in partial front, full front, and full vehicle PPF installations using high-quality films that resist yellowing and offer long-term clarity. Proper preparation, precision cutting, and clean edge finishing ensure results that look seamless and last.
            </p>
            <p>
              Serving Vista, Oceanside, Carlsbad, Encinitas, and the greater San Diego area, we work with Tesla owners, luxury vehicles, trucks, and SUVs looking for professional-grade paint protection installed the right way.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Paint Protection Film FAQs</h2>
          <div className="space-y-6">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What is paint protection film (PPF)?</h3>
              <p className="text-gray-300">Paint protection film is a clear, durable film applied to painted surfaces to protect against rock chips, scratches, road debris, and UV damage while preserving the factory finish.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is PPF worth it in San Diego?</h3>
              <p className="text-gray-300">Yes. Southern California driving conditions, sun exposure, and freeway use make PPF one of the best ways to protect your vehicle's paint long-term.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does PPF last?</h3>
              <p className="text-gray-300">High-quality PPF can last several years when properly installed and maintained. Longevity depends on film type, exposure, and care.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Will PPF damage my paint?</h3>
              <p className="text-gray-300">No. When installed and removed correctly, PPF helps protect factory paint and does not damage it.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Link */}
      <section className="py-12 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold mb-8 text-white text-center">PPF Installation Locations in San Diego</h3>
          <p className="text-lg text-gray-300 text-center mb-8">
            Professional paint protection film services throughout San Diego County:
          </p>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            <a href="/ppf-vista" className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20 hover:border-cyan-500 transition group">
              <p className="text-white font-semibold group-hover:text-cyan-400 transition">PPF Vista</p>
            </a>
            <a href="/ppf-carlsbad" className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20 hover:border-cyan-500 transition group">
              <p className="text-white font-semibold group-hover:text-cyan-400 transition">PPF Carlsbad</p>
            </a>
            <a href="/ppf-oceanside" className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20 hover:border-cyan-500 transition group">
              <p className="text-white font-semibold group-hover:text-cyan-400 transition">PPF Oceanside</p>
            </a>
            <a href="/ppf-encinitas" className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20 hover:border-cyan-500 transition group">
              <p className="text-white font-semibold group-hover:text-cyan-400 transition">PPF Encinitas</p>
            </a>
            <a href="/ppf-rancho-santa-fe" className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20 hover:border-cyan-500 transition group">
              <p className="text-white font-semibold group-hover:text-cyan-400 transition">PPF Rancho Santa Fe</p>
            </a>
            <a href="/ppf-san-diego" className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20 hover:border-cyan-500 transition group">
              <p className="text-white font-semibold group-hover:text-cyan-400 transition">PPF San Diego</p>
            </a>
            <a href="/ppf-la-jolla" className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20 hover:border-cyan-500 transition group">
              <p className="text-white font-semibold group-hover:text-cyan-400 transition">PPF La Jolla</p>
            </a>
            <a href="/ppf-del-mar" className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20 hover:border-cyan-500 transition group">
              <p className="text-white font-semibold group-hover:text-cyan-400 transition">PPF Del Mar</p>
            </a>
            <a href="/ppf-solana-beach" className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20 hover:border-cyan-500 transition group">
              <p className="text-white font-semibold group-hover:text-cyan-400 transition">PPF Solana Beach</p>
            </a>
            <a href="/ppf-poway" className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20 hover:border-cyan-500 transition group">
              <p className="text-white font-semibold group-hover:text-cyan-400 transition">PPF Poway</p>
            </a>
            <a href="/ppf-san-marcos" className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20 hover:border-cyan-500 transition group">
              <p className="text-white font-semibold group-hover:text-cyan-400 transition">PPF San Marcos</p>
            </a>
            <a href="/ppf-escondido" className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20 hover:border-cyan-500 transition group">
              <p className="text-white font-semibold group-hover:text-cyan-400 transition">PPF Escondido</p>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Protect Your Investment Today</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free PPF consultation and quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigateTo("contact")} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Book Consultation
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
    </div>
  )

  // North County Vinyl Wraps Page
  const NorthCountyVinylWrapsPage = () => (
    <div className="pt-20 min-h-screen bg-black">
      <CanonicalTag url="/north-county-vinyl-wraps" />
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">North County Vinyl Wraps</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Professional vinyl wrap installation serving North County San Diego including Vista, Carlsbad, and Oceanside. Transform your vehicle's appearance with <a href="/car-wrap-san-diego" className="text-cyan-400 hover:underline">premium color change wraps</a>, <a href="/commercial-fleet-wraps" className="text-cyan-400 hover:underline">commercial fleet graphics</a>, and custom designs.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Whether you want a complete color transformation, sleek chrome delete, or eye-catching commercial branding, our wrap shop North County has the expertise and materials to bring your vision to life.
            </p>
            <div className="flex gap-4 justify-center mt-8">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => navigateTo("contact")} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Book Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Vinyl Wrap Services */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Vinyl Wrap Services We Offer</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Color Change Wraps</h3>
              <p className="text-gray-300">Complete vehicle transformation with premium Avery, 3M, and specialty films in thousands of colors and finishes.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Wraps</h3>
              <p className="text-gray-300">Accent wraps for roofs, hoods, mirrors, and trim pieces to create unique two-tone designs.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Chrome Delete & Blackout</h3>
              <p className="text-gray-300">Modern blacked-out aesthetic by wrapping chrome trim, badges, and accents in gloss or matte black.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Commercial & Fleet Wraps</h3>
              <p className="text-gray-300">Professional vehicle branding for businesses with custom logos, graphics, and consistent fleet designs.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Custom Printed Wraps</h3>
              <p className="text-gray-300">Unique printed graphics, patterns, and designs created specifically for your vehicle.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/paint-protection-film" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Paint Protection Film</h3>
              <p className="text-gray-300">Self-healing PPF for ultimate protection</p>
            </a>
            <a href="/chrome-delete" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Chrome Delete</h3>
              <p className="text-gray-300">Modern blackout styling and trim</p>
            </a>
            <a href="/commercial-fleet-wraps" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Fleet Wraps</h3>
              <p className="text-gray-300">Professional commercial vehicle branding</p>
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Mr Wrap for North County Vinyl Wraps</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">13 Years of Experience</h3>
              <p className="text-gray-300">13 years of professional vinyl wrap installation in North County.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Experienced Installers</h3>
              <p className="text-gray-300">Experienced technicians with hands-on expertise across major wrap brands.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Premium Wrap Films</h3>
              <p className="text-gray-300">We use only Avery Dennison, 3M, and other top-tier materials.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Proper Disassembly</h3>
              <p className="text-gray-300">Professional component removal for seamless, edge-to-edge coverage.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Areas We Serve */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-8 text-white text-center">Areas We Serve</h2>
          <p className="text-xl text-gray-300 text-center mb-8">
            Proudly serving North County San Diego communities with expert vinyl wrap Vista installation
          </p>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-4xl mx-auto">
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Vista</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Carlsbad</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Oceanside</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">San Marcos</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">Encinitas</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg text-center border border-cyan-500/20">
              <p className="text-white font-semibold">North County</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does a vinyl wrap last?</h3>
              <p className="text-gray-300">Premium vinyl wraps typically last 5-7 years with proper care. Indoor storage and regular maintenance can extend lifespan even further.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does vinyl wrap damage paint?</h3>
              <p className="text-gray-300">No. Professional vinyl wrap installation protects your original paint. When removed properly by experts, the wrap comes off cleanly without damaging the factory finish.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can you wrap leased vehicles?</h3>
              <p className="text-gray-300">Yes! Vinyl wraps are perfect for leased vehicles because they're fully removable. Restore the original appearance before returning the vehicle.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Do wraps require maintenance?</h3>
              <p className="text-gray-300">Minimal maintenance required. Hand wash or touchless car wash recommended. Avoid automatic brush washes which can lift edges over time.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does installation take?</h3>
              <p className="text-gray-300">Full vehicle wraps take 3-5 days depending on vehicle size and complexity. Partial wraps and chrome delete can be completed in 1-2 days.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Transform Your Vehicle?</h2>
          <p className="text-xl text-white/90 mb-8">
            Book a free consultation to discuss your vinyl wrap project
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigateTo("contact")} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Book Consultation
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
    </div>
  )

  // Location Landing Pages Component
  const LocationLandingPage = ({ location: locationSlug }: { location: string }) => {
    const locationContent: Record<string, {
      title: string
      subtitle: string
      city: string
      description: string[]
      localInfo: string
      faqs: { question: string, answer: string }[]
    }> = {
      "san-diego": {
        title: "PPF San Diego",
        subtitle: "Paint Protection Film Installation in San Diego County",
        city: "San Diego",
        description: [
          "San Diego's premier paint protection film installation service serving all of San Diego County. From downtown to Chula Vista, La Jolla to El Cajon, we bring expert PPF installation to San Diego vehicle owners who demand the best.",
          "San Diego's unique climate—coastal salt air, intense UV rays, and busy freeways—makes paint protection essential. Our premium PPF installations protect thousands of San Diego vehicles from the environmental challenges our region presents.",
          "Based in Vista and serving all San Diego communities, we're the trusted choice for luxury, electric, and performance vehicle owners throughout the county. Free mobile quotes available throughout San Diego."
        ],
        localInfo: "Serving: Downtown San Diego, La Jolla, Del Mar, Rancho Bernardo, Poway, Scripps Ranch, Carmel Valley, Pacific Beach, Mission Valley, Hillcrest, North Park, and all San Diego neighborhoods.",
        faqs: [
          {
            question: "Do you serve all of San Diego County?",
            answer: "Yes! We're based in Vista and serve all San Diego County including downtown, coastal communities, east county, and south bay areas. Mobile quotes available."
          },
          {
            question: "Why is PPF important in San Diego?",
            answer: "San Diego's coastal salt air, intense UV exposure, and busy freeways create perfect conditions for paint damage. PPF protects against all these threats."
          },
          {
            question: "What's the best PPF for San Diego weather?",
            answer: "Premium PPF is ideal for San Diego. It offers superior UV protection, won't yellow in our intense sun, and the hydrophobic coating repels salt spray."
          },
          {
            question: "How long does PPF last in San Diego?",
            answer: "Premium PPF can last 10+ years in San Diego climate with proper care. Manufacturer warranty terms vary by film and typically cover defects such as yellowing, cracking, and peeling."
          },
          {
            question: "Do you offer mobile PPF service in San Diego?",
            answer: "We offer mobile quotes throughout San Diego but install at our Vista facility for quality control. Our indoor shop bay gives us proper lighting and cleaner working conditions than a driveway install."
          }
        ]
      },
      "oceanside": {
        title: "PPF Oceanside",
        subtitle: "Paint Protection Film Installation in Oceanside, CA",
        city: "Oceanside",
        description: [
          "Professional paint protection film installation serving Oceanside and North County coastal communities. Oceanside's beachfront location means your vehicle faces constant exposure to salt air, sand, and coastal elements that accelerate paint damage.",
          "Our Oceanside clients include Marines from Camp Pendleton, beach community residents, and commuters along the 5 freeway. We understand the specific challenges Oceanside vehicles face and recommend PPF packages that address these local conditions.",
          "Just minutes from Oceanside, our Vista facility provides convenient PPF service for North County coastal residents. We've protected Oceanside vehicles with premium film selected to handle harsh coastal environments."
        ],
        localInfo: "Serving: Oceanside, Camp Pendleton, South Oceanside, Libby Lake, Fire Mountain, Mission San Luis Rey, and surrounding North County Coastal areas.",
        faqs: [
          {
            question: "Is PPF necessary for Oceanside vehicles?",
            answer: "Absolutely essential. Oceanside's oceanfront location means constant salt air exposure which accelerates paint oxidation. PPF is the best defense."
          },
          {
            question: "How does salt air affect car paint in Oceanside?",
            answer: "Salt air causes paint oxidation, fading, and corrosion. It also etches into clear coat. PPF creates a barrier that prevents salt from reaching your paint."
          },
          {
            question: "Do you install PPF near Camp Pendleton?",
            answer: "Yes! We serve many Camp Pendleton Marines and military families. We're just 15 minutes from base and offer military-friendly scheduling."
          },
          {
            question: "Can PPF protect against beach sand damage?",
            answer: "Yes. PPF protects against sand abrasion, especially on lower panels and rocker panels where sand spray is common on coastal roads."
          },
          {
            question: "How far are you from Oceanside?",
            answer: "We're located in Vista, just 10-15 minutes from Oceanside via Highway 76 or Oceanside Boulevard. Very convenient for Oceanside residents."
          }
        ]
      },
      "vista": {
        title: "PPF Vista",
        subtitle: "Local Paint Protection Film Installation in Vista, CA",
        city: "Vista",
        description: [
          "Vista's local paint protection film experts, conveniently located right here in Vista. As your hometown PPF shop, we provide personalized service with the expertise and materials of a premium installation facility.",
          "Vista residents choose us for our convenient location, competitive pricing, and deep understanding of North County vehicle needs. Whether you commute to San Diego, work locally, or enjoy weekend drives to the mountains or coast, we have PPF solutions for your lifestyle.",
          "Our Vista facility features an indoor installation bay, professional lighting, and cleaner working conditions than a driveway install. As locals ourselves, we're invested in providing Vista with reliable automotive protection services."
        ],
        localInfo: "Serving: Vista, Shadowridge, Windmill Farms, Brengle Terrace, North Vista, South Vista, Rancho Buena Vista, and surrounding Vista neighborhoods.",
        faqs: [
          {
            question: "Where exactly are you located in Vista?",
            answer: "We're centrally located in Vista, easily accessible from Highway 78 and Vista Village Drive. Contact us for exact directions to our facility."
          },
          {
            question: "Do you offer Vista resident discounts?",
            answer: "Contact us for current promotions. We frequently offer special pricing for Vista and North County residents."
          },
          {
            question: "How long have you been in Vista?",
            answer: "We've been serving Vista and North County for 13 years of professional installation experience, building our reputation as a trusted local PPF shop."
          },
          {
            question: "Can I drop off my car in Vista for PPF?",
            answer: "Yes! Most Vista clients prefer drop-off. We can often provide loaner vehicles or arrange pickup/delivery for Vista residents."
          },
          {
            question: "Do you work on Vista commuter vehicles?",
            answer: "Absolutely. Many Vista residents commute to San Diego, making PPF essential for protecting against freeway rock chips. We specialize in commuter vehicle protection."
          }
        ]
      },
      "carlsbad": {
        title: "PPF Carlsbad",
        subtitle: "Paint Protection Film Installation in Carlsbad, CA",
        city: "Carlsbad",
        description: [
          "Premium paint protection film installation serving Carlsbad's luxury vehicle community. Carlsbad's affluent neighborhoods and coastal location make it home to some of North County's finest vehicles—all of which benefit from professional PPF installation.",
          "From Carlsbad Village to Aviara, La Costa to Bressi Ranch, Carlsbad residents understand the value of protecting their automotive investments. Our premium PPF installations preserve your vehicle's beauty despite the challenges of coastal living and daily commuting.",
          "Conveniently located just minutes from Carlsbad in Vista, we've become the trusted PPF provider for Carlsbad's discerning vehicle owners. Our work speaks for itself throughout Carlsbad's luxury car community."
        ],
        localInfo: "Serving: Carlsbad Village, Aviara, La Costa, Bressi Ranch, Calavera Hills, Carlsbad Ranch, Olde Carlsbad, and all Carlsbad communities.",
        faqs: [
          {
            question: "Do you work on luxury vehicles in Carlsbad?",
            answer: "Yes! Carlsbad has many luxury vehicles and we specialize in high-end PPF installations for Mercedes, BMW, Porsche, Tesla, and exotic vehicles."
          },
          {
            question: "Is PPF popular in Carlsbad?",
            answer: "Very popular. Carlsbad residents with luxury and high-performance vehicles understand PPF's value for protection and resale value preservation."
          },
          {
            question: "How far are you from Carlsbad?",
            answer: "Just 10-15 minutes from most Carlsbad areas. Take El Camino Real to Vista Way, very easy drive from anywhere in Carlsbad."
          },
          {
            question: "Do you install PPF on Aviara/La Costa vehicles?",
            answer: "Absolutely. We serve many Aviara and La Costa residents who expect premium service and flawless results. We deliver both."
          },
          {
            question: "Can you protect cars kept near Carlsbad beaches?",
            answer: "Essential for beachside vehicles. Salt air is extremely corrosive. PPF is the best protection against coastal element damage."
          }
        ]
      },
      "encinitas": {
        title: "PPF Encinitas",
        subtitle: "Paint Protection Film Installation in Encinitas, CA",
        city: "Encinitas",
        description: [
          "Professional paint protection film installation serving Encinitas and Cardiff-by-the-Sea. Encinitas' laid-back surf culture meets sophisticated automotive tastes, creating a unique community that values both beach lifestyle and vehicle quality.",
          "Encinitas vehicles face unique challenges: salt air from the Pacific, sand from popular beaches, and the 5 freeway commute to San Diego. Our PPF installations protect against all these factors while maintaining your vehicle's aesthetic appeal.",
          "From Leucadia to Cardiff, Old Encinitas to Olivenhain, we're the trusted PPF provider for North County coastal communities. Our Vista location is just a quick drive from anywhere in Encinitas."
        ],
        localInfo: "Serving: Leucadia, Cardiff-by-the-Sea, Old Encinitas, New Encinitas, Olivenhain, and surrounding coastal communities.",
        faqs: [
          {
            question: "Do you serve Cardiff and Leucadia?",
            answer: "Yes! We serve all Encinitas communities including Cardiff-by-the-Sea, Leucadia, and Olivenhain. Quick drive from any area."
          },
          {
            question: "Is PPF necessary for beach vehicles in Encinitas?",
            answer: "Critical. Vehicles near Moonlight Beach, Swami's, or Beacon's Beach face constant salt air exposure. PPF prevents the paint damage salt causes."
          },
          {
            question: "Do you work on Encinitas surf vehicles?",
            answer: "Absolutely. Many surfers protect their vehicles with PPF to prevent damage from boards, wax, sand, and salt spray. Especially rocker panels and rear areas."
          },
          {
            question: "How do I get from Encinitas to Vista?",
            answer: "Take Encinitas Boulevard east to I-5 north, exit Vista Way. About 15-20 minutes from most Encinitas areas."
          },
          {
            question: "Can PPF handle Encinitas' coastal climate?",
            answer: "Premium PPF is specifically designed for coastal environments. It won't yellow, crack, or peel in salt air conditions."
          }
        ]
      },
      "rancho-santa-fe": {
        title: "PPF Rancho Santa Fe",
        subtitle: "Luxury Paint Protection Film in Rancho Santa Fe, CA",
        city: "Rancho Santa Fe",
        description: [
          "Exclusive paint protection film installation serving Rancho Santa Fe's distinguished automotive community. Rancho Santa Fe represents California luxury living, and the vehicles here deserve the absolute finest protection available.",
          "Our clientele in The Covenant, The Village, and surrounding estates expect perfection. We deliver precisely that with premium PPF installations that maintain your vehicle's pristine appearance while providing invisible, comprehensive protection.",
          "As specialists in luxury and exotic vehicle PPF, we understand the unique requirements of Rancho Santa Fe vehicles. From Ferraris to Bentleys, Range Rovers to Teslas, we've protected the finest automobiles in this exclusive community."
        ],
        localInfo: "Serving: The Covenant, Rancho Santa Fe Village, Fairbanks Ranch, Del Dios, Eden Valley, and surrounding estates.",
        faqs: [
          {
            question: "Do you specialize in luxury vehicle PPF?",
            answer: "Yes. Many of our clients are Rancho Santa Fe residents with high-end vehicles. We specialize in luxury and exotic car PPF installation."
          },
          {
            question: "Can you handle exotic cars from Rancho Santa Fe?",
            answer: "Absolutely. We've installed PPF on Ferraris, Lamborghinis, McLarens, Bentleys, and Rolls-Royces from Rancho Santa Fe estates."
          },
          {
            question: "Do you offer privacy for high-profile clients?",
            answer: "Yes. We understand discretion matters to Rancho Santa Fe clients. Private appointments and confidential service available."
          },
          {
            question: "Is full vehicle PPF recommended for ranch vehicles?",
            answer: "For vehicles at Rancho Santa Fe estates, full vehicle PPF is highly recommended. It protects against dust, pollen, and tree sap common in ranch environments."
          },
          {
            question: "How far are you from Rancho Santa Fe?",
            answer: "About 15-20 minutes via Via de Santa Fe to El Camino Real to Vista. Very convenient for Covenant and Village residents."
          }
        ]
      }
    }

    const locationData = locationContent[locationSlug]
    if (!locationData) {
      navigateTo("home")
      return null
    }

    return (
      <div className="pt-20 min-h-screen bg-black">
        <CanonicalTag url={`/ppf-${locationSlug}`} />
        {/* Hero with Quote Form */}
        <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-5xl font-bold mb-4">{locationData.title}</h1>
                <h2 className="text-2xl text-cyan-400 mb-6">{locationData.subtitle}</h2>
                <p className="text-gray-300 text-lg mb-6">{locationData.localInfo}</p>
                <div className="flex gap-4 mb-8">
                  <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                    <Phone size={18} />
                    858-282-8119
                  </a>
                  <button onClick={() => navigateTo("contact")} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                    <MapPin size={18} />
                    Get Directions
                  </button>
                </div>
              </div>
              
              {/* Above-fold Quote Form */}
              <div className="bg-gray-900 rounded-2xl shadow-xl p-8 border border-cyan-500/20">
                <h3 className="text-2xl font-bold mb-6 text-white">Get Free {locationData.city} Quote</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name *"
                    required
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none text-white"
                  />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone Number *"
                    required
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none text-white"
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email *"
                    required
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none text-white"
                  />
                  <input
                    type="text"
                    name="vehicle"
                    value={formData.vehicle}
                    onChange={handleChange}
                    placeholder="Vehicle (Year, Make, Model)"
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none text-white"
                  />
                  <button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-lg transition">
                    Request {locationData.city} Quote
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto mb-20">
              <h2 className="text-3xl font-bold mb-6 text-white">Professional PPF Installation in {locationData.city}</h2>
              {locationData.description.map((paragraph, index) => (
                <p key={index} className="text-gray-300 mb-4 leading-relaxed text-lg">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Services Grid */}
            <div className="grid md:grid-cols-3 gap-8 mb-20">
              {services.slice(0, 3).map((service, index) => (
                <div key={index} className="bg-gray-900 rounded-xl p-6 border border-cyan-500/20">
                  <img src={service.image} alt={service.title} className="rounded-lg mb-4 w-full h-40 object-cover" />
                  <h3 className="text-xl font-bold mb-2 text-white">{service.title}</h3>
                  <p className="text-gray-400 mb-4">{service.description}</p>
                  <button onClick={() => navigateTo("services")} className="text-cyan-400 hover:text-cyan-300 font-semibold flex items-center gap-2">
                    Learn More <ChevronRight size={16} />
                  </button>
                </div>
              ))}
            </div>

            {/* FAQs */}
            <div className="mb-20">
              <h2 className="text-4xl font-bold mb-12 text-white text-center">{locationData.city} PPF FAQs</h2>
              <div className="max-w-3xl mx-auto space-y-6">
                {locationData.faqs.map((faq, index) => (
                  <div key={index} className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
                    <h3 className="text-xl font-bold mb-3 text-white">{faq.question}</h3>
                    <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Links */}
            <div className="bg-gray-900 rounded-2xl p-8 mb-20">
              <h2 className="text-2xl font-bold mb-6 text-white text-center">Explore More Locations</h2>
              <div className="grid md:grid-cols-3 gap-4">
                <button onClick={() => navigateTo("ppf-vista")} className="bg-black hover:bg-gray-800 text-cyan-400 px-6 py-3 rounded-lg transition text-center">
                  PPF Vista
                </button>
                <button onClick={() => navigateTo("ppf-carlsbad")} className="bg-black hover:bg-gray-800 text-cyan-400 px-6 py-3 rounded-lg transition text-center">
                  PPF Carlsbad
                </button>
                <button onClick={() => navigateTo("ppf-oceanside")} className="bg-black hover:bg-gray-800 text-cyan-400 px-6 py-3 rounded-lg transition text-center">
                  PPF Oceanside
                </button>
              </div>
            </div>

            {/* Gallery Preview */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold mb-8 text-white text-center">Our {locationData.city} Work</h2>
              <div className="grid md:grid-cols-4 gap-4">
                {gallery.slice(0, 8).map((item, index) => (
                  <div key={index} className="relative overflow-hidden rounded-lg group cursor-pointer" onClick={() => navigateTo("gallery")}>
                    <img src={item.url} alt={item.title} className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white font-semibold">View Gallery</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Link Section - San Diego Only */}
            {locationSlug === "san-diego" && (
              <div className="mb-12 text-center">
                <p className="text-lg text-gray-300">
                  <button onClick={() => navigate("/paint-protection-film")} className="text-cyan-400 hover:text-cyan-300 underline transition">
                    View our Paint Protection Film services
                  </button>
                </p>
              </div>
            )}

            {/* Final CTA */}
            <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-2xl p-12 text-center">
              <h2 className="text-4xl font-bold mb-4 text-white">Ready for {locationData.city}'s Best PPF?</h2>
              <p className="text-xl text-white/90 mb-8">
                Schedule your free consultation today
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={() => navigateTo("contact")} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
                  <Calendar size={20} />
                  Schedule Appointment
                </button>
                <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
                  <Phone size={20} />
                  Call: 858-282-8119
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black">
      <Toaster position="top-center" />
      <Navigation />
      
      <Routes>
        {/* Real URL routes for location landing pages */}
        <Route path="/ppf-vista" element={<LocationLandingPage location="vista" />} />
        <Route path="/ppf-carlsbad" element={<LocationLandingPage location="carlsbad" />} />
        <Route path="/ppf-oceanside" element={<LocationLandingPage location="oceanside" />} />
        <Route path="/ppf-encinitas" element={<LocationLandingPage location="encinitas" />} />
        <Route path="/ppf-rancho-santa-fe" element={<LocationLandingPage location="rancho-santa-fe" />} />
        <Route path="/ppf-san-diego" element={<LocationLandingPage location="san-diego" />} />
        
        {/* SEO landing pages */}
        <Route path="/paint-protection-film" element={<PaintProtectionFilmPage />} />
        <Route path="/vinyl-wraps" element={<VinylWrapsPage />} />
        <Route path="/commercial-fleet-wraps" element={<CommercialFleetWrapsPage />} />
        <Route path="/window-tinting" element={<WindowTintingPage />} />
        <Route path="/chrome-delete" element={<ChromeDeletePage />} />
        <Route path="/caliper-painting" element={<CaliperPaintingPage />} />
        <Route path="/headlight-taillight-tint" element={<HeadlightTaillightTintPage />} />
        <Route path="/interior-wrap-trim" element={<InteriorWrapTrimPage />} />
        <Route path="/racing-stripes-graphics" element={<RacingStripesGraphicsPage />} />
        <Route path="/north-county-vinyl-wraps" element={<NorthCountyVinylWrapsPage />} />
        
        {/* New SEO service pages */}
        <Route path="/tesla-ppf-san-diego" element={<TeslaPpfSanDiegoPage />} />
        <Route path="/paint-protection-film-san-diego" element={<PaintProtectionFilmSanDiegoPage />} />
        <Route path="/car-wrap-san-diego" element={<CarWrapSanDiegoPage />} />
        <Route path="/chrome-delete-san-diego" element={<ChromeDeleteSanDiegoPage />} />
        <Route path="/window-tint-san-diego" element={<WindowTintSanDiegoPage />} />
        
        {/* New city location pages */}
        <Route path="/ppf-la-jolla" element={<PpfLaJollaPage />} />
        <Route path="/ppf-del-mar" element={<PpfDelMarPage />} />
        <Route path="/ppf-poway" element={<PpfPowayPage />} />
        <Route path="/ppf-san-marcos" element={<PpfSanMarcosPage />} />
        <Route path="/ppf-escondido" element={<PpfEscondidoPage />} />
        <Route path="/ppf-solana-beach" element={<PpfSolanaBeachPage />} />
        
        {/* Tesla model-specific pages */}
        <Route path="/tesla-model-y-ppf-san-diego" element={<TeslaModelYPpfPage />} />
        <Route path="/tesla-model-3-ppf-san-diego" element={<TeslaModel3PpfPage />} />
        <Route path="/tesla-model-s-ppf-san-diego" element={<TeslaModelSPpfPage />} />
        <Route path="/tesla-model-x-ppf-san-diego" element={<TeslaModelXPpfPage />} />
        <Route path="/tesla-wrap-san-diego" element={<TeslaWrapSanDiegoPage />} />
        <Route path="/color-change-wrap-san-diego" element={<ColorChangeWrapSanDiegoPage />} />
        <Route path="/colored-ppf-san-diego" element={<ColoredPpfSanDiegoPage />} />
        <Route path="/vehicle-wrap-cost-san-diego" element={<VehicleWrapCostSanDiegoPage />} />
        <Route path="/ceramic-coating-san-diego" element={<CeramicCoatingSanDiegoPage />} />
        <Route path="/ppf-vs-ceramic-coating-san-diego" element={<PpfVsCeramicCoatingSanDiegoPage />} />
        <Route path="/bmw-ppf-san-diego" element={<BmwPpfSanDiegoPage />} />
        <Route path="/corvette-ppf-san-diego" element={<CorvettePpfSanDiegoPage />} />
        
        {/* Blog articles */}
        <Route path="/blog/ultimate-guide-to-ppf" element={<BlogPpfGuidePage />} />
        <Route path="/blog/ppf-vs-ceramic-coating" element={<BlogPpfVsCeramicPage />} />
        <Route path="/blog/ppf-longevity-maintenance" element={<BlogPpfLongevityPage />} />
        
        {/* Contact page route */}
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/thank-you" element={<ThankYouPage />} />
        
        {/* All other routes use state-based navigation */}
        <Route path="*" element={
          <>
            {currentPage === "home" && <HomePage />}
            {currentPage === "services" && <ServicesPage />}
            {currentPage === "gallery" && <GalleryPage />}
            {currentPage === "about" && <AboutPage />}
            {currentPage === "contact" && <ContactPage />}
            {currentPage === "privacy" && <PrivacyPage />}
            {currentPage === "terms" && <TermsPage />}
            {currentPage.startsWith("service-") && <ServiceDetailPage serviceId={currentPage.replace("service-", "")} />}
            
            {/* Vehicle Landing Pages */}
            {currentPage === "ppf-tesla-model-3" && <VehicleLandingPage vehicle="tesla-model-3" />}
            {currentPage === "ppf-tesla-model-y" && <VehicleLandingPage vehicle="tesla-model-y" />}
            {currentPage === "ppf-bmw" && <VehicleLandingPage vehicle="bmw" />}
            {currentPage === "ppf-mercedes" && <VehicleLandingPage vehicle="mercedes" />}
            {currentPage === "ppf-4runner" && <VehicleLandingPage vehicle="4runner" />}
            {currentPage === "ppf-range-rover" && <VehicleLandingPage vehicle="range-rover" />}
            
            {/* Location Landing Pages */}
            {currentPage === "ppf-san-diego" && <LocationLandingPage location="san-diego" />}
            {currentPage === "ppf-oceanside" && <LocationLandingPage location="oceanside" />}
            {currentPage === "ppf-carlsbad" && <LocationLandingPage location="carlsbad" />}
            {currentPage === "ppf-encinitas" && <LocationLandingPage location="encinitas" />}
            {currentPage === "ppf-rancho-santa-fe" && <LocationLandingPage location="rancho-santa-fe" />}
          </>
        } />
      </Routes>
      
      <Footer />
      <ChatWidget />
      <QuickActions 
        onBookPackage={() => navigateTo("contact")}
        onBookConsult={() => navigateTo("contact")}
      />
    </div>
  )
}

export default App